import ReactDOM from "react-dom";
import * as DIAG from "storm-react-diagrams";
import BaseLinkModel from "../models/BaseLinkModel";
import { updateLastChange } from "../utilities";
import ContextMenuWidget from "./ContextMenuWidget";
import CustomLinkLayerWidget from "./CustomLinkLayerWidget";
import PropertyPanelWidget from "./PropertyPanelWidget";
import ModuleNodeModel from "../models/ModuleNodeModel";
import CustomNodeLayerWidget from "./CustomNodeLayerWidget";
import Graph from "../Graph";
import PreviewDiagramWidget from "./previews/PreviewDiagramWidget";
import DomToImage from "dom-to-image";
import FileSaver from "file-saver";
import VariableDataModel from "../models/VariableDataModel";
import Utility from "../utilities/Utility";
import { Constant } from '../const';
import NodeRegistry from "../utilities/NodeRegistry";
import CanvasTabManager from "../utilities/CanvasTabManager";
import { ReactDOMComponents } from '../utilities/ReactDOMComponents';
import NewLocalVariableDialog from './dialogs/NewLocalVariableDialog';
import LocalVariablesUsedIn from '../utilities/usedIn/LocalVariableUsedIn';
import _ from "lodash";
import CanvasChangeHistory from "../utilities/CanvasChangeHistory";
import { IntentDataModel } from "../models";
import { defaultServiceContext } from "../contexts";
import { IntentDetails, IntentDetailsMode } from "../page-components/modals";
import { getGlobalSettings } from "../stores";

/**
 * Make types available in JSDocs without importing them since this is not a TS file.
 *
 * @import { DiagramProps } from 'storm-react-diagrams';
 * @import {
 *   DesignModule,
 *   DesignModuleLink
 * } from '../types';
 *
 * @typedef {DiagramProps & {
 *   node: DesignModule
 * }} CustomDiagramWidgetProps
 */

const {
    errorMessageService,
    globalModalsService
} = defaultServiceContext;

export class CustomDiagramWidget extends DIAG.DiagramWidget {
    /** @type {Readonly<CustomDiagramWidgetProps>} */
    props;

    /**
     * Main canvas.
     *
     * @param {CustomDiagramWidgetProps} diagramProps React props
     */
    constructor(diagramProps) {
        super(diagramProps);
        this.props = diagramProps;

        this.state = {
            selectedContext: null
        }

        this.graphModel = null;
        this.canvasNodeRef = null;
        // UI
        this.isSpaceKeyDown = false;
        this.isCtrlKeyDown = false;
        this.currentHighlightedLink = null;
        this.canvas = "";
        this.canvasDefaultGridSize = 0;
        this.mouseDrag = {
            active: false,
            x: 0,
            y: 0
        };
        this.mouseDownPos = {
            x: 0,
            y: 0,
            active: false,
            ox: 0,
            oy: 0
        };
        this.zoomBrackets = [
            0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4, 5
        ];
        window.apm.cache.put(Constant.k.NAMESPACE_DIAGRAM_WIDGET, this.props.clientId, this);
        
        
        this.linkDetached = false;
        this.previewTimer = null;
        this.getGraphModel();
        this.reactDOMComponents = new ReactDOMComponents();
        this.closeValidationDialog = this.closeValidationDialog.bind(this);
        this.handleWheel = this.handleWheel.bind(this);

        // History stack
        this.historyDeleteMap = null;
        this.changeTypes = null;
        // initialize undo/redo for diagram
        this.initializeUndoRedo(this.getFlowModel().serializeDiagram());
        
    }

    getDataModel() {
        if (this.props.node.elemId) {
            return window.apm.getNode(this.props.node.elemId);
        } else {
            return window.apm.getNode(this.props.node.id);
        }
    }

    getGraphModel() {
        const version = this.props.version || 1;
        if (!this.graphModel) {
            this.graphModel = new Graph(this.getDataModel(), false, version);
            this.graphModel.getEngine().clientId = this.props.clientId;
            this.graphModel.getEngine().setCanvas(this.canvasNodeRef);
        }
        return this.graphModel;
    }

    /** @returns {DIAG.DiagramModel & { links: Record<string, DIAG.LinkModel & DesignModuleLink>; }} */
    getFlowModel() {
        return this.getGraphModel().getEngine().getDiagramModel();
    }

    getFlowModelVersion(){
        return window.apm.getCurrentDiagramWidget().props.node.version || 1;
    }

    /**
     * Save model to database
     * @param {number|null} version
     */
    saveDatabase() {
        const module = this.getDataModel();
        if (module) {
            let { elemId, ...changedModule } = _.cloneDeep(module);

            changedModule.versions['1'] = {
                version: changedModule.version,
                flow: this.getFlowModel().serializeDiagram()
            };

            window.apm.setNode(this.props.node.id, changedModule);
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        }
    }

    /**
     * Initializes Undo/Redo for canvas instance 
     * @param {object} serializedChanges 
     */
     initializeUndoRedo(serializedChanges){
        // should be an init method in this
        this.history = new CanvasChangeHistory(serializedChanges);
        this.changeTypes = this.history.getChangeTypes();
        this.historyDeleteMap = [];
    }

    /**
     * Handle redo
     */
    handleRedo() {

        // get change history object
        const nextFlow = this.history.selectNextIndex();
        if(nextFlow) {
            // check for deleted
            const redoMaps = this.historyDeleteMap.pop();

            // if was already marked as deleted, add back to deletedMaps
            if(redoMaps) {
                redoMaps[0].forEach(n => {
                    window.apm.serialized.deletedMap['modules'][this.props.node.id]['nodes'].push(n);
                });
                redoMaps[1].forEach(n => {
                    window.apm.serialized.deletedMap['modules'][this.props.node.id]['links'].push(n);
                });
            }
            
            //Skip redo canvas actions if change history object contains references to nodes not in the application
            if(this.getNodeIsInApplication(nextFlow)) {
                this.drawHistory(this.getFlowModel(), nextFlow, "redo");
                this.saveDatabase();
            } else {
                this.handleRedo();
            }
        }
    }

    /**
     * Handle undo
     */
    handleUndo() {
        // get previous flow object
        const previousFlow = this.history.selectCurrentIndex();
        if(previousFlow) {
            // check for deleted
            const redoDMNodes = [];
            const redoDMLinks = [];

            // does this module have an entry in the window.apm.serialized.deletedMap?
            const modDeletedMap = window.apm.serialized.deletedMap && window.apm.serialized.deletedMap['modules'] && ['modules'] && window.apm.serialized.deletedMap['modules'][this.props.node.id] || null;

            // if so, 
            if (modDeletedMap) {
                if (modDeletedMap['nodes']) {
                    previousFlow.nodes.forEach(n => {
                        const nodeIndex = modDeletedMap['nodes'].findIndex(node => node === n.id);
                        if (nodeIndex > -1) {
                            redoDMNodes.push(modDeletedMap['nodes'][nodeIndex]);
                            modDeletedMap['nodes'].splice(nodeIndex, 1);
                        }

                    });
                }

                if (modDeletedMap['links']) {
                    previousFlow.links.forEach(l => {
                        const linkIndex = modDeletedMap['links'].findIndex(link => link === l.id);
                        if (linkIndex > -1) {
                            redoDMLinks.push(modDeletedMap['links'][linkIndex]);
                            modDeletedMap['links'].splice(linkIndex, 1);
                        }
                    });
                }


            this.historyDeleteMap.push([redoDMNodes, redoDMLinks]);
            }

            //Skip undo canvas actions if change history object contains references to nodes not in the application
            if(this.getNodeIsInApplication(previousFlow)){
                this.drawHistory(this.getFlowModel(), previousFlow, "undo");
                this.history.changeIndexDecrement();
                this.saveDatabase();
                this.handleCustomEvent({ target: "request-update-member" });
            } else {
                this.history.changeIndexDecrement();
                this.handleUndo();
            }
        }
    }

    /**
     * Draw to canvas based on serialized model from Undo/Redo
     * @param {Object} diagramModel Diagram data model
     * @param {Object} serialized Serialized diagram model
     * @param {String} direction undo or redo
     */
    drawHistory(diagramModel, serialized, direction) {

        const changeType = serialized.type;
        const preserved = serialized.preserved;

        // all serialized change objects that are flagged for preserved must have direction reversed
        if (preserved === true) {
            if (direction === "undo") {
                direction = "redo";
            } else if (direction === "redo") {
                direction = "undo";
            }
        }

        // Handles all events that include simple node additions & removals
        serialized.nodes.forEach(element => {

            if (changeType !== this.changeTypes.DELETE &&
                changeType !== this.changeTypes.MOVED_POSITION &&
                changeType !== this.changeTypes.REQUEST_REPLACE &&
                changeType !== this.changeTypes.REQUEST_CLONE &&
                changeType !== this.changeTypes.REQUEST_INSERT &&
                changeType !== this.changeTypes.REQUEST_CREATE_NODE_AT_EVENT &&
                changeType !== this.changeTypes.REQUEST_DUPLICATE) {

                if (direction === "undo") {
                    diagramModel.removeNode(element);
                }
                if (direction === "redo") {
                    diagramModel.addNode(element);
                }
            }
            if (changeType === this.changeTypes.DELETE) {
                if (direction === "redo") {
                    diagramModel.removeNode(element);
                }
                if (direction === "undo") {
                    diagramModel.addNode(element);
                }
            }

        });
        
        // Selected free floating point(s) have been moved to a new position on the canvas
        if (changeType === this.changeTypes.MOVED_POINT_POSITION) {
            serialized.models.forEach(position => {
                const originalPosition = position.initial;
                const newPosition = position.new;
                const pointModel = position.nodeModel;

                if (direction === "undo") {
                    pointModel.x = originalPosition.x;
                    pointModel.y = originalPosition.y;
                    updateLastChange(pointModel.link.props);
                }
                if (direction === "redo") {
                    pointModel.x = newPosition.x;
                    pointModel.y = newPosition.y;
                }
            });
        }

        // Selected node(s) have been moved to a new position on the canvas
        if (changeType === this.changeTypes.MOVED_POSITION) {
            serialized.models.forEach(position => {
                const originalPosition = position.initial;
                const newPosition = position.new;
                const nodeModel = position.nodeModel;
                const nodePorts = Object.keys(nodeModel.ports);

                if (direction === "undo") {
                    nodeModel.x = originalPosition.x;
                    nodeModel.y = originalPosition.y;
                    this.getFlowModel().updateNodePos(nodeModel);
                    nodeModel.props.lastLocalChange = serialized.previousTimestamp;
                    
                    //Assign previous timestamp to any existing links connected to the node's ports
                    nodePorts.forEach(port => {
                        const linkID = Object.keys(nodeModel.ports[port].links)[0];
                        if(linkID){
                            nodeModel.ports[port].links[linkID].props.lastLocalChange = serialized.previousTimestamp;
                        }
                    });
                }
                if (direction === "redo") {
                    nodeModel.x = newPosition.x;
                    nodeModel.y = newPosition.y;
                    this.getFlowModel().updateNodePos(nodeModel);
                }
            });
        }
        
        // Handles Clone, Paste Clone, & Create New Object (at mouse event)

        if(changeType === this.changeTypes.REQUEST_DUPLICATE || changeType ===  this.changeTypes.REQUEST_CREATE_NODE_AT_EVENT){
            const node = serialized.nodes[0];
            const appObj = serialized.nodes[1];

            if (direction === "undo") {
                diagramModel.removeNode(node);
                this.handleHistoryAppObj(appObj, direction);
            }
            if (direction === "redo") {
                diagramModel.addNode(node);
                this.handleHistoryAppObj(appObj, direction);
            }
        }

        // Handles both "Replace with..." and "Clone and Replace" events
        if (changeType === this.changeTypes.REQUEST_REPLACE || changeType ===  this.changeTypes.REQUEST_CLONE) {
            const oldNode = serialized.nodes[0];
            const newNode = serialized.nodes[1];
            const appObj = serialized.nodes[2];

            if (direction === "undo") {
                diagramModel.removeNode(newNode);
                diagramModel.addNode(oldNode);
                serialized.links.forEach((link, i) => {
                    const oldTarget = (oldNode.getPorts())[link.targetPort.name];
                    const oldSource = (oldNode.getPorts())[link.sourcePort.name];
                    if (link.sourcePort.parentNode.id === newNode.id) {
                        link.setSourcePort(null);
                        link.setSourcePort(oldSource);
                    }
                    if (link.targetPort.parentNode.id === newNode.id) {
                        link.setTargetPort(null);
                        link.setTargetPort(oldTarget);
                    }
                    link.props.lastLocalChange = serialized.previousTimestamp;
                });
                this.handleHistoryAppObj(appObj, direction);
            }
            if (direction === "redo") {
                diagramModel.removeNode(oldNode);
                diagramModel.addNode(newNode);

                serialized.links.forEach(link => {
                    const newTarget = (newNode.getPorts())[link.targetPort.name];
                    const newSource = (newNode.getPorts())[link.sourcePort.name];
                    if (link.sourcePort.parentNode.id === oldNode.id) {
                        link.setSourcePort(null);
                        link.setSourcePort(newSource);
                    }
                    if (link.targetPort.parentNode.id === oldNode.id) {
                        link.setTargetPort(null);
                        link.setTargetPort(newTarget);
                    }
                });
                this.handleHistoryAppObj(appObj, direction);
            }
        }

        // Handles the insertion of a node into a transition. All inserts entail 1 original link and 2 new links
        if (changeType === this.changeTypes.REQUEST_INSERT) {
            const oldLink = serialized.links[0];
            const newLink = serialized.links[1];
            const newNode = serialized.nodes[0];
            const appObj = serialized.nodes[1];
            const oldTarget = newLink.targetPort;
            const newTarget = (newNode.getPorts())[oldLink.targetPort.name];

            if (direction === "undo") {
                oldLink.setTargetPort(null);
                oldLink.setTargetPort(oldTarget);
                diagramModel.removeLink(newLink);
                diagramModel.removeNode(newNode);
                oldLink.props.lastLocalChange = serialized.previousTimestamp;
                this.handleHistoryAppObj(appObj, direction);
            }
            if (direction === "redo") {
                oldLink.setTargetPort(null);
                diagramModel.addNode(newNode);
                oldLink.setTargetPort(newTarget);
                diagramModel.addLink(newLink);
                this.handleHistoryAppObj(appObj, direction);
            }
        }

        // Free floating point on link has been added
        if (changeType === this.changeTypes.NEW_LINK_POINT) {
            if (direction === "undo") {
                serialized.linkView.props.link.removePoint(serialized.linkView.props.link.getPoints()[serialized.index]);
                serialized.linkView.props.link.props.lastLocalChange = serialized.previousTimestamp;
            }
            if (direction === "redo") {
                serialized.linkView.props.link.addPoint(serialized.point, serialized.index);
                updateLastChange(serialized.linkView.props.link.props);
            }
        }

        // Free floating point on link has been removed
        if (changeType === this.changeTypes.REMOVE_LINK_POINT) {
            if (direction === "undo") {
                serialized.linkView.props.link.addPoint(serialized.point, serialized.index);
                serialized.linkView.props.link.props.lastLocalChange = serialized.previousTimestamp;
                
            }
            if (direction === "redo") {
                serialized.linkView.props.link.removePoint(serialized.linkView.props.link.getPoints()[serialized.index]);
                updateLastChange(serialized.linkView.props.link.props);
                
            }
        }

        //This is for new link targets. Model array length greater than 2 means the link isn't new
        if (changeType === this.changeTypes.NEW_LINK_TARGET && serialized.models.length === 2) {
            const oldTarget = serialized.models[0];
            const newTarget = serialized.models[1];
            const changedLink = serialized.links[0];

            if (direction === "undo") {
                changedLink.setTargetPort(null);
                changedLink.setTargetPort(oldTarget);
                changedLink.props.lastLocalChange = serialized.previousTimestamp;
            }
            if (direction === "redo") {
                changedLink.setTargetPort(null);
                changedLink.setPreviousPort("target", oldTarget);
                changedLink.setTargetPort(newTarget);
            }
        } 

        // This is for new links. Model array will not have items
        if (changeType === this.changeTypes.NEW_LINK_TARGET && serialized.models.length === 0) {
            const changedLink = serialized.links[0];
            const linkId = changedLink.id;

            if (direction === "undo") {
                diagramModel.removeLink(changedLink);
                delete changedLink.targetPort.links[linkId];
                delete changedLink.sourcePort.links[linkId];
            }
            if (direction === "redo") {
                diagramModel.addLink(changedLink);
            }
        }

        if (changeType === this.changeTypes.NEW_LINK_SOURCE) {
            const oldSource = serialized.models[0];
            const newSource = serialized.models[1];
            const changedLink = serialized.links[0];

            if (direction === "undo") {
                changedLink.setSourcePort(null);
                changedLink.setSourcePort(oldSource);
                changedLink.props.lastLocalChange = serialized.previousTimestamp;
            }
            if (direction === "redo") {
                changedLink.setSourcePort(null);
                changedLink.setPreviousPort("source", oldSource);
                changedLink.setSourcePort(newSource);
            }
        }
        // Handles the removal of links
        serialized.links.forEach(link => {
            
            if (changeType === this.changeTypes.DELETE) {
                if (direction === "redo") {
                    diagramModel.removeLink(link);
                }
                if (direction === "undo") {
                    diagramModel.addLink(link);
                }
            }

            if (changeType === this.changeTypes.REQUEST_PASTE) {
                if (direction === "redo") {
                    diagramModel.addLink(link);
                }
                if (direction === "undo") {
                    diagramModel.removeLink(link);
                }
            }
        });
        
        // Enable full refresh and then refresh
        this.getGraphModel().getEngine().clearRepaintEntities();
        this.saveDatabase();
        this.forceUpdate();
    }

    /**
     * Handle undo/redo with application level objects created by canvas events
     * @param {object} appObj application level object to recreate or delete
     * @param {string} direction undo or redo
     */

    handleHistoryAppObj(appObj, direction){
        const treeDom = $('#tl_container');
        const treeContainerParent = (appObj.type === "object") ?  appObj.props.nodeTypeName : appObj.type + "s";

        if (direction === "undo") {
            treeDom.jstree().delete_node(appObj);
            Object.keys(window.apm.serialized.components).forEach(type => {
                if (window.apm.serialized.components.hasOwnProperty(type)) {
                    window.apm.serialized.components[type].forEach((node,i)=>{
                        if (appObj.id === window.apm.serialized.components[type][i].id) {
                            window.apm.serialized.components[type].splice(i, 1);
                        }
                    });
                }
            });
        }
        if (direction === "redo") {
            window.apm.addNode(appObj);
            treeDom.jstree().create_node(treeContainerParent, Utility.getTreeNode(appObj),"last");
        }
    }

    /**
     * Check current change history object if it contains or references nodes that do not exist in application
     * @param {object} serialized serialized change object
     */
     getNodeIsInApplication(serialized){

        let nodeIsInApplication = true;

        //check if each node exists in the application
        serialized.nodes.forEach(node => {

            const inAppModules = window.apm.serialized.components.modules.findIndex(mod => mod.id === (node.props.parentComponentID || node.id));
            const inAppObjects = window.apm.serialized.components.objects.findIndex(obj => obj.id === (node.props.parentComponentID || node.id));
            let inDeletedMap = false;

            //check Deleted Maps
            if(window.apm.serialized.deletedMap && window.apm.serialized.deletedMap['application-objects']){
                const inDeletedMapsObjects = window.apm.serialized.deletedMap['application-objects'].findIndex(objId => objId === (node.props.parentComponentID || node.id));
                if(inDeletedMapsObjects > -1){
                    inDeletedMap = true;
                }
            }

            if(window.apm.serialized.deletedMap && window.apm.serialized.deletedMap['application-modules']){
                const inDeletedMapsModules = window.apm.serialized.deletedMap['application-modules'].findIndex(modId => modId === (node.props.parentComponentID || node.id));
                if(inDeletedMapsModules > -1){
                    inDeletedMap = true;
                }
            }

            //check Serialized
            if(!(inAppModules > -1 || inAppObjects > -1) && inDeletedMap){
                nodeIsInApplication = false;
            }
        });

        //Check if each link source or target references a node that exists in the application
        serialized.links.forEach(link => {
            const sourcePortParent = link.sourcePort.parentNode.props.parentComponentID;
            const targetPortParent = link.targetPort.parentNode.props.parentComponentID;
            const sourceInAppModules = window.apm.serialized.components.modules.findIndex(mod => mod.id === sourcePortParent);
            const targetInAppModules = window.apm.serialized.components.modules.findIndex(mod => mod.id === targetPortParent);
            const sourceInAppObjects = window.apm.serialized.components.objects.findIndex(obj => obj.id === sourcePortParent);
            const targetInAppObjects = window.apm.serialized.components.objects.findIndex(obj => obj.id === targetPortParent);
            let inDeletedMap = false;

            //check Deleted Maps
            if(window.apm.serialized.deletedMap && window.apm.serialized.deletedMap['application-objects']){
                const inDeletedMapsObjects = window.apm.serialized.deletedMap['application-objects'].findIndex(objId => objId === (sourcePortParent || targetPortParent));
                if(inDeletedMapsObjects > -1){
                    inDeletedMap = true;
                }
            }

            if(window.apm.serialized.deletedMap && window.apm.serialized.deletedMap['application-modules']){
                const inDeletedMapsModules = window.apm.serialized.deletedMap['application-modules'].findIndex(modId => modId === (sourcePortParent || targetPortParent));
                if(inDeletedMapsModules > -1){
                    inDeletedMap = true;
                }
            }

            if(!((sourceInAppModules > -1 || sourceInAppObjects > -1) && (targetInAppModules > -1 || targetInAppObjects > -1)) && inDeletedMap) {
                nodeIsInApplication = false;
            }
        });

        return nodeIsInApplication;
    }

    /**
     * Draw to canvas based on serialized model
     * @param {Object} diagramModel Diagram data model
     * @param {Object} serialized Serialized diagram model
     */
    drawFromSerialized(diagramModel, serialized) {
        // Remember user selection
        CanvasTabManager.updateTabRepoVersion(diagramModel.id, window.apm.serialized.repoVersion);
        console.time("drawFromSerialized");
        const selected = diagramModel.getSelectedItems().map(element => {
            return element ? element.id : null;
        });
        // Reset model
        diagramModel.clearElements();
        this.clearLinkTerminals();
        // Draw from scratch
        for (var i = 0; i < serialized.nodes.length; i++) {
            const node = serialized.nodes[i];
            const newNode = new NodeRegistry.r[node._class](node.name, node.x, node.y, node.props, node.id);
            if (selected.indexOf(newNode.id) !== -1) {
                newNode.selected = true;
            }
            // CM TODO: isDrop, graphModel, version
            diagramModel.addNode(newNode);
        }
        for (var i = 0; i < serialized.links.length; i++) {
            const link = serialized.links[i];
            if (link.source && link.target) {
                const sourcePort = (diagramModel.getNode(link.source).getPorts())[link.sourcePort];
                const targetPort = (diagramModel.getNode(link.target).getPorts())[link.targetPort];
                var newLink = new NodeRegistry.r[link._class](sourcePort, targetPort, link.props, link.id, link.points);
                if (selected.indexOf(newLink.id) !== -1) {
                    newLink.selected = true;
                }
                const numPoints = link.points.length;
                newLink.getFirstPoint().updateLocation({
                    x: link.points[0].x,
                    y: link.points[0].y
                });
                newLink.getLastPoint().updateLocation({
                    x: link.points[numPoints - 1].x,
                    y: link.points[numPoints - 1].y
                });
                diagramModel.addLink(newLink);
            }
        }
        // Enable full refresh and then refresh
        this.getGraphModel().getEngine().clearRepaintEntities();
        this.saveDatabase();
        this.forceUpdate();
        console.timeEnd("drawFromSerialized");
    }

    closeValidationDialog() {
        this.reactDOMComponents.unmountComponentAtNode($(".validator_container"));
        w2popup.close();
    }

    // TODO: if transition contains output from source node, then remove data from source node
    initToggleDisableFlowFromLink(link, value, nodes, links){
        const targetNode = link.getTargetNode();
        const descendents = [];
        const getTargetNodeDescendents = (targetNode, descendents) => {
            const targetNodeDescendents = targetNode.getOutLinks().map(ol => ol.getTargetNode());

            descendents.push(targetNode);

            // for every descendent of target node, find descendents
            if(targetNodeDescendents.length > 0){
                targetNodeDescendents.forEach(targetNode => {
                    const targetNodeNotInDescendents = descendents.findIndex(des => des.id === targetNode.id) === -1;
                    const targetNodeDoesNotPointToSourceNode = targetNode.getOutLinks().filter(ol => ol.targetPort.parentNode.id === link.sourcePort.parentNode.id).length === 0;
                    const linkDoesNotOriginateFromTargetNode = targetNode.getOutLinks().filter(ol => ol.sourcePort.parentNode.id === link.sourcePort.parentNode.id).length === 0;
                    if(targetNodeNotInDescendents && targetNodeDoesNotPointToSourceNode && linkDoesNotOriginateFromTargetNode){
                        return getTargetNodeDescendents(targetNode, descendents);
                    } else {
                        if(!targetNodeDoesNotPointToSourceNode){
                            descendents.push(targetNode);
                        }

                    }
                });
            }
            else {
                // break recursion here

            }
        };

        getTargetNodeDescendents(targetNode, descendents);

        this.toggleDisableFlowFromLink(link, value, nodes, links, descendents, true);
    }

    toggleDisableFlowFromLink(link, value, nodes, links, descendents){
        
        // toggle link model via props.config
        link.props.config.disabledInFlow = value; // do not use link.setConfig here as that is the callee to this function

        const stopAtTargetNode = (targetNode) =>{
            const targetNodeHasSingleLink = targetNode.getInLinks().length === 1;
            const targetNodelinksAllConditional = Object.values(links)
                .filter(l => l.targetPort.parentNode.id === targetNode.id)
                .filter(l => l.props.config.condition.length === 0)
                .length === 0;

            const lastConditionalLinkToBeToggled = Object.values(links)
                .filter(l => l.targetPort.parentNode.id === targetNode.id)
                .filter(l => l.props.config.disabledInFlow !== value)
                .length === 0;

            const targetNodeAlreadyToggled = targetNode.getConfig('disabledInFlow') === value;

            const getTargetNodeHasOtherNonToggledInLinks = (targetNode) => {
                return targetNode.getInLinks()
                    .filter(l => l.props.config.disabledInFlow !== value)
                    .filter(l => descendents.findIndex(d => d.id === l.sourcePort.parentNode.id) === -1);
            };
            const targetNodeHasOtherNonToggledInLinks = getTargetNodeHasOtherNonToggledInLinks(targetNode).length > 0;

            // single link implies default path out of node, do not stop at this node
            if(targetNodeHasSingleLink) return  false;

            // if all links are conditional and this is the last link to be toggled return ${value}
            if(targetNodelinksAllConditional && !lastConditionalLinkToBeToggled){
                return value;
            }

            // if targetNode is already toggled or there are other available inlinks
            if(targetNodeAlreadyToggled || targetNodeHasOtherNonToggledInLinks){
                return true;
            }

            return false;
        };

        const targetNode = link.getTargetNode();

        // break recursion?
        if(stopAtTargetNode(targetNode)) return;
        // toggle node model
        // disable node via setConfig
        targetNode.setConfig('disabledInFlow', value);
        
        //DEVDES2-1023: if node type is module and it is being enabled (value === false), enable parent in components.modules
        if(targetNode.type === 'module' && !value){
            const mod = window.apm.serialized.components.modules.find(mod => mod.id === targetNode.props.parentComponentID);
            if(mod.props.config['disabledInFlow']){
                mod.props.config['disabledInFlow'] = value;
            }    
        }

        const sourceLinks = targetNode.getOutLinks();

        // recurse through each nodes' links
        sourceLinks.forEach(link => {
            this.toggleDisableFlowFromLink(link, value, nodes, links, descendents);
        });
        
        window.apm.setSerializedChanged();
        window.apm.refreshApplicationTree();
    

    }

    // toggles node's disabledInFlow data in jstree node
    toggleDisableModuleFromFlow({module, value}, jstree){
        jstree.get_node(module.id).li_attr.class = (value) ? 'disabledInFlow' : '';
    }

    /**
     * Used to preserve original link and create new link attached to inserted object
     * @param {Object} selected_link original link on which object is being inserted 
     * @param {Object} targetNode new object inserted onto original link
     * @param {Object} points mouse position for new object insertion
     */
    
    linkClone(selected_link, targetNode, points) {

        const selectedLink = _.cloneDeep(selected_link);

        const targetPort = (this.getFlowModel().getNode(selectedLink.target).getPorts())[selectedLink.targetPort];
        const sourcePort = (targetNode.getPorts())[selectedLink.sourcePort];

        var newLink = new NodeRegistry.r[selectedLink._class](sourcePort, targetPort, selectedLink.props, Utility.uuid4(), selectedLink.points);
                
            newLink.getFirstPoint().updateLocation({
                x: points.x,
                y: points.y
            });
            
            newLink.getLastPoint().updateLocation({
                x: selectedLink.points[1].x,
                y: selectedLink.points[1].y
            });
            
            updateLastChange(newLink.props);

            // blankConfig is for the new link coming off the newly inserted object
            const defaultConfig = {
                condition: [], 
                description: "", 
                disabledInFlow: false,
                inputs: [],
                name: "",
                serializedCondition: "true"
            };

            newLink.props.config = defaultConfig;

            delete newLink.props.lastSavedChange;
            return newLink;
    }

    /**
     * Handle custom events
     * @param {Object} event Custom event
     * @param {Function|null} callback
     */
    handleCustomEvent(event) {
        
        var filename;
        if (this.getDataModel()) {
            filename = (this.getDataModel().type + "_" + this.getDataModel().name).replace(/([^a-z0-9]+)/gi, '_');
        }
        const treeM = $('#tm_container');
        const treeL = $('#tl_container');
        var contextContainer = null;
        const historyNodes = [];
        const historyLinks = [];
        let previousTimestamp;
        const commands = event.commands;
        
        switch (event.target) {
            case "request-insert":
                window.apm.postEventToCurrentView({ target: "request-copy" });
                window.apm.postEventToCurrentView({ target: "request-delete" });
                const selected_clipboard = window.apm.cache.get(Constant.k.NAMESPACE_DATATRANSFER, "clipboard");
                const selected_link = selected_clipboard.links[0];
                

                // links array starts with the selected link
                historyLinks.push(this.currentHighlightedLink);

                previousTimestamp = this.currentHighlightedLink.props.lastLocalChange;

                // nodes
                const nodePoints = this.getClosestGridPoint(event.event);
                nodePoints.x = nodePoints.x - 100;
                nodePoints.y = nodePoints.y - 55;
                const insertNodeClass = event.node._class;
                const insertNodeName = window.apm.getEligibleTreeName(treeM, event.node.name, event.node.type, event.node.props.nodeTypeName);
                const insertNewNode = new NodeRegistry.r[insertNodeClass](insertNodeName, nodePoints.x, nodePoints.y, event.node.props);
                if (event.node.type === "module") {

                    insertNewNode.props.ref = event.node.id;
                }
                insertNewNode.props.parentComponentID = event.node.id;
                insertNewNode.props.timeAddedToFlow = window.apm.timestamp;

                this.getFlowModel().addNode(insertNewNode);

                historyNodes.push(insertNewNode);

                //application level node
                historyNodes.push(event.node);
                
                const nodeTarget = (insertNewNode.getPorts())[selected_link.targetPort];
                this.currentHighlightedLink.setTargetPort(null);
                this.currentHighlightedLink.setTargetPort(nodeTarget);
                
                //Creates new link off of new node with same properteries as the original
                const newInsertLink = this.linkClone(selected_link, insertNewNode, nodePoints);
                
                this.getFlowModel().addLink(newInsertLink);

                historyLinks.push(newInsertLink);
                
                this.history.saveChanges(this.changeTypes.REQUEST_INSERT, { nodes: historyNodes, links: historyLinks, previousTimestamp: previousTimestamp });
                
                this.forceUpdate();
                this.saveDatabase();
                break;
            case "request-replace":
                window.apm.postEventToCurrentView({ target: "request-copy" });
                
                const getHighlightedNode = this.getFlowModel().getSelectedItems();
                
                getHighlightedNode.forEach((node,i) => {
                    if(i === 0){
                        previousTimestamp = node.props.lastLocalChange;
                        historyNodes.push(node);
                    } else {
                        historyLinks.push(node.getLink())
                    }
                });
                
                // nodes
                const replaceNodePoints = this.getClosestGridPoint(event.event);
                replaceNodePoints.x = replaceNodePoints.x - 100;
                replaceNodePoints.y = replaceNodePoints.y - 55;
                
                const replaceNodeClass = event.node._class;
                const replaceNodeName = window.apm.getEligibleTreeName(treeM, event.node.name, event.node.type, event.node.props.nodeTypeName);
                const replacementNode = new NodeRegistry.r[replaceNodeClass](replaceNodeName, replaceNodePoints.x, replaceNodePoints.y, event.node.props);
                
                if (event.node.type === "module") {
                    replacementNode.props.ref = event.node.id;
                }
                replacementNode.props.parentComponentID = event.node.id;
                replacementNode.props.timeAddedToFlow = window.apm.timestamp;
                
                historyNodes.push(replacementNode);
                //application level node
                historyNodes.push(event.node);
                
                window.apm.updateDeletedModulesMap(this.props.node, 'nodes', getHighlightedNode[0].id);
                this.getFlowModel().removeNode(getHighlightedNode[0]);
                
                this.getFlowModel().addNode(replacementNode);

                // 
                historyLinks.forEach(link => {
                    const newTarget = (replacementNode.getPorts())[link.targetPort.name];
                    const newSource = (replacementNode.getPorts())[link.sourcePort.name];
                    if(link.sourcePort.parentNode.id === getHighlightedNode[0].id) {
                        link.setSourcePort(null);
                        link.setSourcePort(newSource);
                    }
                    if(link.targetPort.parentNode.id === getHighlightedNode[0].id) {
                        link.setTargetPort(null);
                        link.setTargetPort(newTarget);
                    }
                }); 
                
                this.history.saveChanges(this.changeTypes.REQUEST_REPLACE, { nodes: historyNodes, links: historyLinks, previousTimestamp: previousTimestamp });

                this.forceUpdate();
                this.saveDatabase();
                    break;
            case "request-create-node-at-event":
                const points = this.getClosestGridPoint(event.event);
                const className = event.node._class;
                const newName = window.apm.getEligibleTreeName(treeM, event.node.name, event.node.type, event.node.props.nodeTypeName);
                
                var newNode = new NodeRegistry.r[className](newName, points.x, points.y, event.node.props);
                if (event.node.type === "module") {

                    newNode.props.ref = event.node.id;
                }
                newNode.props.parentComponentID = event.node.id;
                newNode.props.timeAddedToFlow = window.apm.timestamp;

                // CM TODO: isDrop, graphModel, version
                this.getFlowModel().addNode(newNode);
                
                historyNodes.push(newNode);
                
                //application level node
                historyNodes.push(event.node);
                
                this.history.saveChanges(this.changeTypes.REQUEST_CREATE_NODE_AT_EVENT, { nodes: historyNodes });

                this.forceUpdate();
                this.saveDatabase();
                break;
            case "request-disable-flow-from-link":
                const {nodes, links} = this.getFlowModel();
                const {link, value} = event;
                this.initToggleDisableFlowFromLink(link, value, nodes, links);
                this.saveDatabase();

                break;
            case "request-disable-module-from-flow":
                this.toggleDisableModuleFromFlow(event, treeL.jstree());
                this.saveDatabase();
                
                break;
            // toggle disabled elements in canvas
            case "request-toggle-disabled-elements":
                const toggleModule = this.getDataModel();
                const toggleFlow = toggleModule.versions[1].flow;
                const disabledNodes = toggleFlow.links.filter(n => n.props.config.disabledInFlow);
                const disabledLinks = toggleFlow.links.filter(l => l.props.config.disabledInFlow);
                
                // optimization: do not forceUpdate if canvas does not have disabled elements
                if([...disabledLinks, ...disabledNodes].length === 0){
                    return;
                }
                else{
                    this.getGraphModel().getEngine().clearRepaintEntities();
                    this.forceUpdate();
                }
                break;
            case "request-reload-data-model":
                const dataModel = this.getDataModel();
                // this dataModel may be null for newly deleted modules, if so return
                if(!dataModel){
                    return;
                }
                const flow = dataModel.versions[dataModel.version].flow;

                this.drawFromSerialized(this.getFlowModel(), flow);
                if (event.type && event.type !== "variable") {
                    this.hidePropertyPanel();
                }
                this.configEvaluation();
                this.forceUpdate();
                break;
            case "request-download-json":
                const blob = new Blob([JSON.stringify(window.apm.exportNode(this.props.node.id))], { type: "text/plain;charset=utf-8" });
                FileSaver.saveAs(blob, filename + "_D2.json");
                break;
            case "request-download-image":
                this.requestDownloadImageHandler(filename);
                break;
            case "request-render-preview":
                if (this.previewTimer !== null) {
                    clearTimeout(this.previewTimer);
                    this.previewTimer = null;
                }
                this.previewTimer = setTimeout(() => {
                    if (event.node && event.domContext && event.domContext.offset()) {
                        const graphApp = new Graph(event.node, true);
                        const offset = event.domContext.offset();
                        $(".preview_wrapper").css({
                            top: offset.top - 89,
                            left: offset.left + event.domContext.width() + 10
                        }).show(0, () => {
                            const instance = ReactDOM.render(<PreviewDiagramWidget 
                                node={event.node} 
                                isTargetModuleDisabledInFlow={event.isTargetModuleDisabledInFlow}
                                diagramEngine={graphApp.getEngine()} />, $(".preview_wrapper").children(".preview_container").get(0));
                            instance.fit();
                        });
                    }
                }, 500);
                break;
            case "request-cancel-preview":
                if (this.previewTimer !== null) {
                    clearTimeout(this.previewTimer);
                    this.previewTimer = null;
                }
                $(".preview_wrapper").hide();
                break;
            case "request-hide-property-panel":
                this.hidePropertyPanel();
                break;
            case "request-change-language":
                if (event.event.hasOwnProperty("language")) {
                    window.apm.setBasic("language", event.event.language);
                    this.handleCustomEvent({ target: "request-reload-data-model" });
                    this.handleCustomEvent({ target: "request-update-language-setting" });
                }
                break;
            case "request-update-member":
                if (treeM) {
                    this.getFlowModel().updateMemberTree(treeM, event.type, event.node);
                    if(commands && commands.hidePropertyPanel){
                        this.hidePropertyPanel();
                    }
        
                }
                break;
            case "request-update-property":
                const selected = this.getFlowModel().getSelectedItems();
                var numSelected = 0;
                var selection = null;
                for (var i = 0; i < selected.length; i++) {
                    if (selected[i] instanceof DIAG.NodeModel || selected[i] instanceof DIAG.LinkModel) {
                        numSelected++;
                        selection = selected[i];
                    }
                }
                if (numSelected === 0) {
                    this.hidePropertyPanel();
                } else if (numSelected === 1 && selection) {
                    if (selection instanceof DIAG.NodeModel || selection instanceof DIAG.LinkModel) {
                        this.showPropertyPanel(selection);
                    }
                }
                break;
            case "request-update-variables":
                if (this.props.node.needUpdateVariables) {
                    delete this.props.node.needUpdateVariables;
                    this.saveDatabase();
                }
                break;
            case "request-component-panel":
                var node;
                if (event.event) {
                    node = window.apm.getNode(event.event.id) || event.event;
                }
                else {
                    node = this.getDataModel();
                }
                var proxyNode;
                if (node.type === "variable") {
                    proxyNode = new VariableDataModel(node.props, node.id);
                } else {
                    proxyNode = new NodeRegistry.r[node["_class"]](node.name, 0, 0, node.props, node.id);
                    if (node["_class"] === "ModuleNodeModel") {
                        proxyNode.setProperty("ref", node.id);
                    }
                    proxyNode.setProperty("parentComponentID", node.id);
                }
                proxyNode.setWriteProxy((key, value) => {
                    const nodeCopy = JSON.parse(JSON.stringify(node));
                    nodeCopy.name = proxyNode.getConfig("name");
                    nodeCopy.props = proxyNode.serialize().props;
                    window.apm.setNode(nodeCopy.id, nodeCopy);

                    window.apm.postEventToAllViews({ target: "request-update-evaluation" });

                    if (treeL) {
                        treeL.jstree().rename_node(node.id, proxyNode.getConfig("name"));
                        // treeL.jstree().get_node(node.id).props = node.props;
                    }
                });
                if(!event.suppressPropertyPanel){
                    this.showPropertyPanel(proxyNode, null, event.toolBarNode);
                    if(event.shouldFocusInput){
                        Utility.focusPropertyPanelInput();
                    }
                }
                // DEVDES2-977
                if (event.toolBarNode) {
                    window.apm.reactDOMComponents.render({ name: 'SettingWidget', props: { serialized: window.apm.serialized }, $el: $('#settings') });
                    
                }
                break;
            case "request-transition-property-panel":
                var linkProperties = window.apm.serialized.globalIntents.findIndex(intent => intent.id === event.event.id);
                var proxyNode;
                proxyNode = new NodeRegistry.r[window.apm.serialized.globalIntents[linkProperties].conditionProperties["_class"]](window.apm.serialized.globalIntents[linkProperties].conditionProperties.props.config.name, 0, 0, window.apm.serialized.globalIntents[linkProperties].conditionProperties.props, window.apm.serialized.globalIntents[linkProperties].conditionProperties.id);
                this.showPropertyPanel(proxyNode, null, event.toolBarNode);
                break;
            case "request-globalModule-component-panel":
                var proxyNode = new NodeRegistry.r[event.event.node["_class"]](event.event.node.name, 0, 0, event.event.node.props, event.event.node.id);
                proxyNode.intent = event.event.node.intent;

                this.showPropertyPanel(proxyNode, null, event.toolBarNode);
                break;
            case "request-variable-property-panel":
                
                this.showPropertyPanel(event.event.node, null, false);
                break;
            case "request-node-property-panel":
                
                this.showPropertyPanel(event.event.node, null, false);
                break;
            case "request-remove-local-variable":
                var localVariables = this.getDataModel().localVariables;
                var variables = _.filter(localVariables, { id: event.varIdOrName });
                if (variables.length > 0) {
                    var _index = localVariables.indexOf(variables[0]);
                    if (_index !== -1) {
                        // update serialzed.deletedMap
                        const lvUsedIn = LocalVariablesUsedIn.getVariableUsedIn(localVariables[_index], event.module, window.apm.serialized);
                        if (lvUsedIn.length === 0) {
                            window.apm.removeLocalVariableReferences(event.module, localVariables[_index],lvUsedIn);
                            window.apm.updateDeletedModulesMap(this.getDataModel(), 'localVariables', localVariables[_index].id);
                            window.apm.deleteModuleInput(localVariables[_index],window.apm.getCurrentDiagramWidget().props.node.id, window.apm.serialized);
                            // remove from collection
                            $('#tm_container').jstree().delete_node(localVariables[_index]);
                            this.getDataModel().localVariables.splice(_index, 1);
                        }
                        else {
                            window.apm.notification.post("error", "Cannot delete variable since it is used in the module");
                            
                        }
                       
                    }
                }
                this.hidePropertyPanel();
                this.saveDatabase();
                break;
            case "request-update-evaluation":
                this.configEvaluation();
                break;
            case "request-select-matching":
                if (event.event.target === "tree") {
                    const model = event.event.model;
                    if (model) {
                        if (treeM) {
                            treeM.jstree().deselect_all();
                            treeM.jstree().select_node(event.event.model.getID());
                        }
                        if (model.props.ref) {
                            const appNode = window.apm.getNode(model.props.ref);
                            if (appNode) {
                                $("#tl_q").val(appNode.name);
                                // See DEVDES2-889: Application Panel Search Optimization
                                //$("#tl_search").trigger("change");
                            }
                        }
                    }
                } else {
                    if (treeL) {
                        treeL.jstree().deselect_all();
                    }
                    const zoomFactor = this.getFlowModel().getZoomLevel() / 100;
                    var element;
                    var center;
                    if (event.event.type === "object" || event.event.type === "module") {
                        element = this.getFlowModel().getNode(event.event.id);
                        center = {
                            x: (this.getCanvas().width() / 2 - 70) / zoomFactor - element.x,
                            y: (this.getCanvas().height() / 2 - 140) / zoomFactor - element.y,
                        }
                    }
                    if (event.event.type === "link") {
                        element = this.getFlowModel().getLink(event.event.id);
                        const firstPoint = element.points[0];
                        const lastPoint = element.points[element.points.length - 1];
                        center = {
                            x: (this.getCanvas().width() / 2) / zoomFactor - (firstPoint.x + lastPoint.x) / 2,
                            y: (this.getCanvas().height() / 2 - 140) / zoomFactor - (firstPoint.y + lastPoint.y) / 2,
                        }
                    }
                    if (element) {
                        this.getFlowModel().clearSelection();
                        element.setSelected(true);
                        this.showPropertyPanel(element);

                        const dX = center.x * zoomFactor;
                        const dY = center.y * zoomFactor;
                        this.getFlowModel().setOffset(dX, dY);
                        this.getCanvas().css({
                            "background-position-x": dX + "px",
                            "background-position-y": dY + "px"
                        });
                        this.clearLinkTerminals();
                        this.forceUpdate();
                    }
                    if (event.event.type === "variable") {
                        const varNode = window.apm.getVariable(event.event.id, this.getDataModel().id);
                        const proxyVar = new VariableDataModel(varNode.props, varNode.id);
                        proxyVar.setWriteProxy((key, value) => {
                            const newProps = proxyVar.serialize().props;
                            for (var i = 0; i < this.getDataModel().localVariables.length; i++) {
                                if (this.getDataModel().localVariables[i].id === proxyVar.id) {
                                    this.getDataModel().localVariables[i].name = proxyVar.getConfig("name");
                                    this.getDataModel().localVariables[i].props = newProps;
                                }
                            }
                            if (treeM) {
                                treeM.jstree().rename_node(event.event.id, proxyVar.getConfig("name"));
                                // treeM.jstree().get_node(event.event.id).props = newProps;
                            }
                        });
                        proxyVar.__isLocalComponent = true;
                        this.showPropertyPanel(proxyVar);
                    }
                }
                break;
            case "request-clear-selection":
                if (this.getFlowModel().getSelectedItems().length) {
                    this.hidePropertyPanel();
                }
                this.getFlowModel().clearSelection();
                this.forceUpdate();
                if (treeM) {
                    treeM.jstree().deselect_all();
                }
                break;
            case "request-redo":
                const redo = _.debounce(() => {this.handleRedo();}, 100);
                redo();
                break;
            case "request-undo":
                const undo = _.debounce(() => {this.handleUndo();}, 100);
                undo();
                break;
            case "request-refresh":
                if (event.event) {
                    if (event.event.id && event.event.type === "node") {
                        var node = this.getFlowModel().getNode(event.event.id);
                        if (node && node !== undefined) {

                            if (event.event.upDatedField && event.event.newValue) {
                                if (event.event.upDatedField === "name" || event.event.upDatedField === "wftName") {
                                    node.name = event.event.newValue;
                                    node.wftName = event.event.newValue;
                                }
                            } if (event.event.upDatedField && event.event.props) {
                                node.props = event.event.props;
                            }
                            if (event.event.error && event.event.node) { this.getGraphModel().getEngine().enableRepaintEntities([event.event.node]); }
                            else {
                                this.getGraphModel().getEngine().enableRepaintEntities([node]);
                            }
                            this.forceUpdate();
                        }

                    } else if (event.event.id && event.event.type === "link") {
                        const link = this.getFlowModel().getLink(event.event.id);
                        if (link) {
                            if (event.event.updatedLink !== undefined) link.props = event.event.updatedLink.props;
                            this.getGraphModel().getEngine().enableRepaintEntities([link]);
                            this.forceUpdate();
                        }
                    }
                } else {
                    this.forceUpdate();
                }

                this.getGraphModel().getEngine().clearRepaintEntities();
                 
                // do not update the open module's timestamp for Application module description updates 
                if(event.event && event.event.upDatedField) {
                    this.saveDatabase(); 
                }
                //update the database if link is updated
                if(event.event && event.event.updatedLink) {
                   this.saveDatabase(); 
                }
                this.configEvaluation();
                break;
            case "request-refresh-links":
                this.getGraphModel().getEngine().clearRepaintEntities();
                this.getGraphModel().getEngine().linksThatHaveInitiallyRendered = {};
                try {
                    this.refs["linkLayer-" + this.props.clientId].forceUpdate();
                } catch (e) {
                    console.log("linkLayer- not defined");
                }
                this.saveDatabase();
                break;
            case "request-validate":

                var title = window.l.popup.validator_results;
                if (event.appValidation) {
                    title = '<div id="app-exit"><div class="bg" onmousedown="event.stopPropagation()" onclick=window.apm.openValidationWindow();><i class="fe icon-arrow-left"></i></div><div class="peel"></div></div>' + title;
                }
                window.apm.popup(title,
                    '<div class="validator_container"></div>',
                    '<button class="w2ui-btn primary">' + window.l.popup.close + '</button> ',
                    [
                        ".validator_container",

                        () => {
                            const $el = $(".validator_container");
                            this.reactDOMComponents.renderDialog({
                                name: 'validatorDialog',
                                props: {
                                    node: this.props.node,
                                    closeDialog: this.closeValidationDialog,
                                    clientId: this.props.getclientid,
                                    singleerrors: true
                                },
                                $el
                            });

                            $(".w2ui-popup").find(".w2ui-btn.primary").on("click", () => {
                                this.closeValidationDialog();
                            });
                        

                        }
                    ],
                    700, // height
                    null, // node
                    null, // treenode
                    false // showClose
                );
                break;
            case "request-application-validation":

                window.apm.popup(window.l.popup.validator_results,
                    '<div class="validator_container"></div>',
                    '<button class="w2ui-btn primary">' + window.l.popup.close + '</button> ',
                    [
                        ".validator_container",

                        () => {
                        

                            const $el = $(".validator_container");
                            this.reactDOMComponents.renderDialog({
                                name: 'validatorDialog',
                                props: {
                                    closeDialog: this.closeValidationDialog
                                },
                                $el
                            });

                            $(".w2ui-popup").find(".w2ui-btn.primary").on("click", () => {
                                this.closeValidationDialog();
                            });

                            $(".w2ui-popup").find(".w2ui-btn.primary").on("click", () => {
                                this.closeValidationDialog();
                            });
                        

                        }
                    ],
                    700, // height
                    null, // node
                    null, // treenode
                    false // showClose
                );
                break;
            case "request-change-view":
                this.getFlowModel().viewRenderMode = event.event.view;
                break;
            case "request-select-all":
                this.handleSelected("select-all");
                break;
            case "request-copy":
                this.handleSelected("copy");
                break;
            case "clone":
                const clone_clipboard_1 = window.apm.cache.get(Constant.k.NAMESPACE_DATATRANSFER, "clipboard");
                var idMap = {};
                
                if (!clone_clipboard_1 || !clone_clipboard_1.hasOwnProperty("nodes")) {
                    return;
                }
                if (clone_clipboard_1.nodes.length > 0) {
                    diagramModel.clearSelection();
                }
                var basePoint = { x: 0, y: 0 };
                var offset = { x: 0, y: 0 };
                if (event.event) {
                    basePoint = this.getGraphModel().getEngine().getRelativeMousePoint({
                        clientX: event.event.x,
                        clientY: event.event.y
                    });
                    var leftmostNode = _.minBy(clone_clipboard_1.nodes, (o) => { return o.x; });
                    var topmostNode = _.minBy(clone_clipboard_1.nodes, (o) => { return o.y; });
                    offset.x = leftmostNode.x;
                    offset.y = topmostNode.y;
                }

                for (var i = 0; i < clone_clipboard_1.nodes.length; i++) {
                    const clone_element = clone_clipboard_1.nodes[i];
                    var proposedPos = { x: clone_element.x - offset.x + basePoint.x, y: clone_element.y - offset.y + basePoint.y };
                    var finalPos = this.getClosestGridPoint(null, proposedPos);
                    var parentName = window.apm.getEligibleNodeName(treeL, clone_element.name, clone_element.type, clone_element.props.nodeTypeName);
                    var treeDom = $('#tl_container');
                    let cloned_parent_node;
                    if (clone_element.type === "object") {
                        cloned_parent_node = {
                            id: Utility.uuid4(),
                            name: parentName,
                            type: clone_element.type,
                            props: clone_element.props,
                            _class: clone_element._class
                        };
                    }
                    else {
                        cloned_parent_node = window.apm.getNode(clone_element.props.parentComponentID);
                        cloned_parent_node.id = Utility.uuid4();
                        cloned_parent_node.name = parentName;
                    }
                    if (cloned_parent_node.props.parentComponentID) {
                        cloned_parent_node.props.parentComponentID = undefined;
                    }
                    cloned_parent_node.props.createdTime = window.apm.timestamp;
                    if (cloned_parent_node.type === "module") {
                        cloned_parent_node.props.ref = cloned_parent_node.id;
                        for (var n = 0; n < cloned_parent_node.versions[1].flow.nodes.length; n++) {
                            var existNode = cloned_parent_node.versions[1].flow.nodes[n].id;
                            cloned_parent_node.versions[1].flow.nodes[n].id = Utility.uuid4();
                            if (JSON.stringify(cloned_parent_node.versions[1].flow.links).includes(existNode)) {
                                (cloned_parent_node.versions[1].flow.links = JSON.parse(JSON.stringify((cloned_parent_node.versions[1].flow.links)).replace(new RegExp(existNode, 'gi'), cloned_parent_node.versions[1].flow.nodes[n].id)));
                            }
                        }
                        for (var l = 0; l < cloned_parent_node.versions[1].flow.links.length; l++) {
                            cloned_parent_node.versions[1].flow.links[l].id = Utility.uuid4();
                        }
                    }
                    if (parentNode.localVariables && parentNode.localVariables.length > 0) {
                        parentNode.localVariables.forEach(lv => {
                            const varId = lv.id;
                            lv.id = Utility.uuid4();
                            lv.props.parentModule = parentNode.id;
                            lv.props.usedIn = [];
                            parentNode.versions[1].flow.nodes.forEach(no => {
                              if(JSON.stringify(no.props.config).includes(varId))no.props.config=JSON.parse(JSON.stringify(no.props.config).replace(new RegExp(varId, 'gi'), lv.id));
                            })
                        })
                    }
                    window.apm.addNode(parentNode);
                    
                    // add type name folder if not exist
                    if (parentNode.type === "object") {
                        if (!treeDom.jstree().get_node(parentNode.props.nodeTypeName)) {
                            // add type name folder if not exist
                            treeDom.jstree().create_node("objects", {
                                "id": cloned_parent_node.props.nodeTypeName,
                                "text": cloned_parent_node.props.nodeTypeName,
                                "type": "objects",
                                "state": { "opened": true },
                                "children": [Utility.getTreeNode(cloned_parent_node)]
                            }, "last");
                        } else {
                            treeDom.jstree().create_node(cloned_parent_node.props.nodeTypeName, Utility.getTreeNode(cloned_parent_node), "last");
                        }
                    }
                    else {
                        treeDom.jstree().create_node(cloned_parent_node.type + "s", Utility.getTreeNode(cloned_parent_node), "last");
                    }

                    // add cloned node to canvas
                    const new_cloned_node = new NodeRegistry.r[cloned_parent_node._class](cloned_parent_node.name, finalPos.x, finalPos.y, cloned_parent_node.props);
                    new_cloned_node.props.parentComponentID = cloned_parent_node.id;
                    new_cloned_node.props.timeAddedToFlow = window.apm.timestamp;
                    if (new_cloned_node.type === "module") {
                        new_cloned_node.props.ref = cloned_parent_node.id;
                    }
                    idMap[cloned_parent_node.id] = {
                        id: new_cloned_node.id,
                        offset: { x: finalPos.x - clone_element.x, y: finalPos.y - clone_element.y }
                    };
                    new_cloned_node.props.timeAddedToFlow = window.apm.timestamp;
                    window.apm.postEventToCurrentView({ target: "request-delete", event: { id: clone_element.id } });
                    // CM TODO: isDrop, graphModel, version
                    diagramModel.addNode(new_cloned_node);
                    new_cloned_node.setSelected(true);
                    new_cloned_node.updateVariables();
                    
                    historyNodes.push(new_cloned_node);
                }

                this.handleCustomEvent({ target: "request-update-member" });
                
                this.history.saveChanges(this.changeTypes.CLONE, { nodes: historyNodes });
                
                this.forceUpdate();
                this.saveDatabase();
                break;
            case "request-duplicate":
                const clipboard = window.apm.cache.get(Constant.k.NAMESPACE_DATATRANSFER, "clipboard");
                if (!clipboard || !clipboard.hasOwnProperty("nodes")) {
                    return;
                }

                var diagramModel = this.getFlowModel();
                if (clipboard.nodes.length > 0) {
                    diagramModel.clearSelection();
                }

                var basePoint = { x: 0, y: 0 };
                var offset = { x: 0, y: 0 };
                if (event.event) {
                    basePoint = this.getGraphModel().getEngine().getRelativeMousePoint({
                        clientX: event.event.x,
                        clientY: event.event.y
                    });
                    var leftmostNode = _.minBy(clipboard.nodes, (o) => { return o.x; });
                    var topmostNode = _.minBy(clipboard.nodes, (o) => { return o.y; });
                    offset.x = leftmostNode.x;
                    offset.y = topmostNode.y;
                }

                var idMap = {};
                for (var i = 0; i < clipboard.nodes.length; i++) {
                    const duplicate_node = clipboard.nodes[i];
                    var proposedPos = { x: duplicate_node.x - offset.x + basePoint.x, y: duplicate_node.y - offset.y + basePoint.y };
                    var finalPos = this.getClosestGridPoint(null, proposedPos);
                    var parentName = window.apm.getEligibleNodeName(treeL, duplicate_node.name, duplicate_node.type, duplicate_node.props.nodeTypeName);
                    var treeDom = $('#tl_container');
                    var parentNode;
                    if (duplicate_node.type === "object") {
                        parentNode = {
                            id: Utility.uuid4(),
                            name: parentName,
                            type: duplicate_node.type,
                            props: duplicate_node.props,
                            _class: duplicate_node._class
                        };
                    }
                    else {
                        var node = window.apm.getNode(duplicate_node.props.parentComponentID);
                        var parentNode = JSON.parse(JSON.stringify(node));
                        parentNode.id = Utility.uuid4();
                        parentNode.name = parentName;
                    }
                    if (parentNode.props.parentComponentID) {
                        parentNode.props.parentComponentID = undefined;
                    }
                    if (parentNode.type === "module") {
                        parentNode.props.ref = parentNode.id;
                        for (var n = 0; n < parentNode.versions[1].flow.nodes.length; n++) {
                            var existNode = parentNode.versions[1].flow.nodes[n].id;
                            parentNode.versions[1].flow.nodes[n].id = Utility.uuid4();
                            if (JSON.stringify(parentNode.versions[1].flow.links).includes(existNode)) {
                                (parentNode.versions[1].flow.links = JSON.parse(JSON.stringify((parentNode.versions[1].flow.links)).replace(new RegExp(existNode, 'gi'), parentNode.versions[1].flow.nodes[n].id)));
                            }
                        }
                        for (var l = 0; l < parentNode.versions[1].flow.links.length; l++) {
                            parentNode.versions[1].flow.links[l].id = Utility.uuid4();
                        }
                    }
                    if (parentNode.localVariables && parentNode.localVariables.length > 0) {
                        parentNode.localVariables.forEach(lv => {
                            const varId = lv.id;
                            lv.id = Utility.uuid4();
                            lv.props.parentModule = parentNode.id;
                            lv.props.usedIn = [];
                            parentNode.versions[1].flow.nodes.forEach(no => {
                              if(JSON.stringify(no.props.config).includes(varId))no.props.config=JSON.parse(JSON.stringify(no.props.config).replace(new RegExp(varId, 'gi'), lv.id));
                            })
                        })
                    }
                    window.apm.addNode(parentNode);
                    // add type name folder if not exist
                    if (parentNode.type === "object") {
                        if (!treeDom.jstree().get_node(parentNode.props.nodeTypeName)) {
                            // add type name folder if not exist
                            treeDom.jstree().create_node("objects", {
                                "id": parentNode.props.nodeTypeName,
                                "text": parentNode.props.nodeTypeName,
                                "type": "objects",
                                "state": { "opened": true },
                                "children": [Utility.getTreeNode(parentNode)]
                            }, "last");
                        } else {
                            treeDom.jstree().create_node(parentNode.props.nodeTypeName, Utility.getTreeNode(parentNode), "last");
                        }
                    }
                    else {
                        treeDom.jstree().create_node(parentNode.type + "s", Utility.getTreeNode(parentNode), "last");
                    }
                    
                    var nodeName = parentNode.name;
                    var newNode = new NodeRegistry.r[parentNode._class](nodeName, finalPos.x, finalPos.y, parentNode.props);
                    newNode.props.parentComponentID = parentNode.id;
                    newNode.props.timeAddedToFlow = window.apm.timestamp;

                    if (newNode.type === "module") {
                        newNode.props.ref = parentNode.id;
                    }
                    idMap[parentNode.id] = {
                        id: newNode.id,
                        offset: { x: finalPos.x - duplicate_node.x, y: finalPos.y - duplicate_node.y }
                    };
                    
                    // CM TODO: isDrop, graphModel, version
                    diagramModel.addNode(newNode);
                    newNode.setSelected(true);
                    newNode.updateVariables();
                    historyNodes.push(newNode);

                    //application level node
                    historyNodes.push(parentNode);
                }
                this.handleCustomEvent({ target: "request-update-member" });
                
                this.history.saveChanges(this.changeTypes.REQUEST_DUPLICATE, { nodes: historyNodes });

                this.forceUpdate();
                this.saveDatabase();
                break;
            case "request-clone":
                const clone_clipboard = window.apm.cache.get(Constant.k.NAMESPACE_DATATRANSFER, "clipboard");
                var idMap = {};
                if (!clone_clipboard || !clone_clipboard.hasOwnProperty("nodes")) {
                    return;
                }

                const getHighlightedNodeClone = this.getFlowModel().getSelectedItems();
                getHighlightedNodeClone.forEach((node,i) => {
                    if(i === 0){
                        previousTimestamp = node.props.lastLocalChange;
                        historyNodes.push(node);
                    } else {
                        historyLinks.push(node.getLink());
                    }
                });

                if (clone_clipboard.nodes.length > 0) {
                    this.getFlowModel().clearSelection();
                }
                var basePoint = { x: 0, y: 0 };
                var offset = { x: 0, y: 0 };
                if (event.event) {
                    basePoint = this.getGraphModel().getEngine().getRelativeMousePoint({
                        clientX: event.event.x,
                        clientY: event.event.y
                    });
                    var leftmostNode = _.minBy(clone_clipboard.nodes, (o) => { return o.x; });
                    var topmostNode = _.minBy(clone_clipboard.nodes, (o) => { return o.y; });
                    offset.x = leftmostNode.x;
                    offset.y = topmostNode.y;
                }

                for (var i = 0; i < clone_clipboard.nodes.length; i++) {
                    const clone_node = clone_clipboard.nodes[i];
                    var proposedPos = { x: clone_node.x - offset.x + basePoint.x, y: clone_node.y - offset.y + basePoint.y };
                    var finalPos = this.getClosestGridPoint(null, proposedPos);
                    var parentName = window.apm.getEligibleNodeName(treeL, clone_node.name, clone_node.type, clone_node.props.nodeTypeName);
                    var treeDom = $('#tl_container');
                    var parentNode;
                    
                    if (clone_node.type === "object") {
                        parentNode = {
                            id: Utility.uuid4(),
                            name: parentName,
                            type: clone_node.type,
                            props: clone_node.props,
                            _class: clone_node._class
                        };
                    }
                    else {
                        // get parent node module
                        parentNode=_.cloneDeep(window.apm.getNode(clone_node.props.parentComponentID));
                        //parentNode=JSON.parse(JSON.stringify(window.apm.getNode(clone_node.props.parentComponentID)))
                        parentNode.id = Utility.uuid4();
                        parentNode.name = parentName;

                    }
                    if (parentNode.props.parentComponentID) {
                        // TODO: why not delete parentComponentID?
                        parentNode.props.parentComponentID = undefined;
                    }
                    // -
                    parentNode.props.createdTime = window.apm.timestamp;
                    updateLastChange(parentNode.props);
                    delete parentNode.props.lastSavedChange;
                    // -
                    if (parentNode.type === "module") {
                        parentNode.props.ref = parentNode.id;
                        for (var n = 0; n < parentNode.versions[1].flow.nodes.length; n++) {
                            // create new node id and update any references in links 
                            const new_cloned_node_id = Utility.uuid4();
                            var existNode = parentNode.versions[1].flow.nodes[n].id;
                            parentNode.versions[1].flow.nodes[n].id = new_cloned_node_id;
                            if (JSON.stringify(parentNode.versions[1].flow.links).includes(existNode)) {
                                (parentNode.versions[1].flow.links = JSON.parse(JSON.stringify((parentNode.versions[1].flow.links)).replace(new RegExp(existNode, 'gi'), new_cloned_node_id)));
                            }
                        }
                        // new ids for all cloned links
                        for (var l = 0; l < parentNode.versions[1].flow.links.length; l++) {
                            parentNode.versions[1].flow.links[l].id = Utility.uuid4();

                        }
                    }
                    if (parentNode.localVariables && parentNode.localVariables.length > 0) {
                        parentNode.localVariables.forEach(lv => {
                            const varId = lv.id;
                            lv.id = Utility.uuid4();
                            lv.props.parentModule = parentNode.id;
                            lv.props.usedIn = [];
                            parentNode.versions[1].flow.nodes.forEach(no => {
                              if(JSON.stringify(no.props.config).includes(varId))no.props.config=JSON.parse(JSON.stringify(no.props.config).replace(new RegExp(varId, 'gi'), lv.id));
                            })
                        })
                    }
                    // add to components collection
                    window.apm.addNode(parentNode);
                    
                    // add type name folder if not exist
                    if (parentNode.type === "object") {
                        if (!treeDom.jstree().get_node(parentNode.props.nodeTypeName)) {
                            // add type name folder if not exist
                            treeDom.jstree().create_node("objects", {
                                "id": parentNode.props.nodeTypeName,
                                "text": parentNode.props.nodeTypeName,
                                "type": "objects",
                                "state": { "opened": true },
                                "children": [Utility.getTreeNode(parentNode)]
                            }, "last");
                        } else {
                            treeDom.jstree().create_node(parentNode.props.nodeTypeName, Utility.getTreeNode(parentNode), "last");
                        }
                    }
                    else {
                        treeDom.jstree().create_node(parentNode.type + "s", Utility.getTreeNode(parentNode), "last");
                    }

                    var nodeName = parentNode.name;
                    var newNode = new NodeRegistry.r[parentNode._class](nodeName, finalPos.x, finalPos.y, parentNode.props); // TODO: could use `cloned_node.props`?
                    // maintain disabledInFlow props and configs from origional cloned node
                    newNode.props.colorClass = clone_node.props.colorClass;
                    newNode.props.config.disabled = clone_node.props.config.disabled;
                    newNode.props.parentComponentID = parentNode.id;
                    newNode.props.timeAddedToFlow = window.apm.timestamp;
                    if(parentNode.type === "module")newNode.props.config.inputs=window.apm.getModuleInputs(parentNode);
                    newNode.x = clone_node.x;
                    newNode.y = clone_node.y;

                    // -
                    if (newNode.type === "module") {
                        newNode.props.ref = parentNode.id;
                    }
                    idMap[parentNode.id] = {
                        id: newNode.id,
                        offset: { x: finalPos.x - clone_node.x, y: finalPos.y - clone_node.y }
                    };
                    
                    historyNodes.push(newNode);

                    //application level node
                    historyNodes.push(parentNode);

                    window.apm.updateDeletedModulesMap(this.props.node, 'nodes', getHighlightedNodeClone[0].id);
                    this.getFlowModel().removeNode(getHighlightedNodeClone[0]);
                    
                    // CM TODO: isDrop, graphModel, version
                    this.getFlowModel().addNode(newNode);

                    // forEach
                    historyLinks.forEach(link => {
                        const newTarget = (newNode.getPorts())[link.targetPort.name];
                        const newSource = (newNode.getPorts())[link.sourcePort.name];
                        if(link.sourcePort.parentNode.id === getHighlightedNodeClone[0].id) {
                            link.setSourcePort(null);
                            link.setSourcePort(newSource);
                        }
                        if(link.targetPort.parentNode.id === getHighlightedNodeClone[0].id) {
                            link.setTargetPort(null);
                            link.setTargetPort(newTarget);
                        }
                    }); 
                    
                    //window.apm.postEventToCurrentView({ target: "request-delete", event: { id: clone_node.id } });
                    newNode.setSelected(true);
                    newNode.updateVariables();
                    this.showPropertyPanel(newNode);
                }
                this.handleCustomEvent({ target: "request-update-member" });
                
                this.history.saveChanges(this.changeTypes.REQUEST_CLONE, { nodes: historyNodes, links: historyLinks, previousTimestamp: previousTimestamp });
                
                this.forceUpdate();
                this.saveDatabase();
                this.getCanvas().focus();
                break;
            case "request-paste":
                const paste_clipboard = window.apm.cache.get(Constant.k.NAMESPACE_DATATRANSFER, "clipboard");
                if (!paste_clipboard || !paste_clipboard.hasOwnProperty("nodes")) {
                    return;
                }

                const flowModel = this.getFlowModel();
                if (paste_clipboard.nodes.length > 0) {
                    flowModel.clearSelection();
                }

                var basePoint = { x: 0, y: 0 };
                var offset = { x: 0, y: 0 };

                // Position nodes
                if (event.event) {
                    basePoint = this.getGraphModel().getEngine().getRelativeMousePoint({
                        clientX: event.event.x,
                        clientY: event.event.y
                    });
                    const leftmostNode = _.minBy(paste_clipboard.nodes, (o) => { return o.x; });
                    const topmostNode = _.minBy(paste_clipboard.nodes, (o) => { return o.y; });
                    offset.x = leftmostNode.x;
                    offset.y = topmostNode.y;
                }

                // Add nodes to diagram
                var idMap = {};
                for (var i = 0; i < paste_clipboard.nodes.length; i++) {
                    const paste_node = paste_clipboard.nodes[i];
                    const proposedPos = { x: paste_node.x - offset.x + basePoint.x, y: paste_node.y - offset.y + basePoint.y };
                    const finalPos = this.getClosestGridPoint(null, proposedPos);
                    const newName = paste_node.name;
                    const new_paste_node = new NodeRegistry.r[paste_node._class](newName, finalPos.x, finalPos.y, paste_node.props);

                    // Pasted nodes should not be disabled
                    new_paste_node.props.config=window.apm.resetObjectPropsAndConfig(new_paste_node, paste_node);
                    idMap[paste_node.id] = {
                        id: new_paste_node.id,
                        offset: { x: finalPos.x - paste_node.x, y: finalPos.y - paste_node.y }
                    };

                    updateLastChange(new_paste_node.props);
                    new_paste_node.props.timeAddedToFlow = window.apm.timestamp;

                    // CM TODO: isDrop, graphModel, version
                    flowModel.addNode(new_paste_node);
                    new_paste_node.setSelected(true);
                    new_paste_node.updateVariables();
                    
                    historyNodes.push(new_paste_node)
                }

                // Add links to diagram
                for (var i = 0; i < paste_clipboard.links.length; i++) {
                    const paste_link = paste_clipboard.links[i];
                    if (idMap[paste_link.source] && idMap[paste_link.target]) {
                        const sourcePort = (flowModel.getNode(idMap[paste_link.source].id).getPorts())[paste_link.sourcePort];
                        const targetPort = (flowModel.getNode(idMap[paste_link.target].id).getPorts())[paste_link.targetPort];
                        const new_paste_link = new NodeRegistry.r[paste_link._class](sourcePort, targetPort, paste_link.props);
                        // pasted links should not be disabled
                        new_paste_link.props.config=window.apm.resetObjectPropsAndConfig(new_paste_link, paste_link);
                        const numPoints = paste_link.points.length;
                        // position links
                        new_paste_link.getFirstPoint().updateLocation({
                            x: paste_link.points[0].x + idMap[paste_link.source].offset.x,
                            y: paste_link.points[0].y + idMap[paste_link.source].offset.y
                        });
                        new_paste_link.getLastPoint().updateLocation({
                            x: paste_link.points[numPoints - 1].x + idMap[paste_link.target].offset.x,
                            y: paste_link.points[numPoints - 1].y + idMap[paste_link.target].offset.y
                        });

                        updateLastChange(new_paste_link.props);
                        delete new_paste_link.props.lastSavedChange;

                        flowModel.addLink(new_paste_link);
                        new_paste_link.setSelected(true);

                        historyLinks.push(new_paste_link);
                    }
                }

                // TODO: Create function to let CanvasChangeHistory handle empty history objects???
                // Paste must have at least one node
                if(historyNodes.length > 0) {
                    this.history.saveChanges(this.changeTypes.REQUEST_PASTE, { nodes: historyNodes, links: historyLinks });
                }

                this.forceUpdate();
                this.saveDatabase();
                this.getCanvas().focus();
                window.apm.reactDOMComponents.render({
                    name: 'SettingWidget',
                    props: { serialized: window.apm.serialized },
                    $el: $('#settings'),
                });

                break;
            case "request-delete":
                if (event.event && event.event.id) {
                    var element = this.getFlowModel().getNode(event.event.id);
                    if (element)
                        element.setSelected(true);
                }
                if(event.type) {
                    this.handleSelected("delete",event.type);
                } else {
                    this.handleSelected("delete");
                }
                break;
            case "request-save-module":
                this.handleSelected("save", event);
                break;
            case "request-open-module":
                const tabs = CanvasTabManager.getTabs();
                const elemIndex = this.getDataModel().versions[this.getDataModel().version].flow.nodes.findIndex(element => element.id === event.event.id);
                const elem = this.getDataModel().versions[this.getDataModel().version].flow.nodes[elemIndex];
                // version tells Graph which flow version to open on Common Modules
                // const version =  (this.getDataModel().props.isCommonModule) ? this.getDataModel().version : (event.event.version) ? event.event.version : 1;
                const version =  event.event.version || 1;
                const closable = (elem.props.ref === window.apm.serialized.rootModule) ? false : true;
                
                if (!window.apm.hasPermission(elem.props.ref, "read")) {
                    window.apm.notification.post("error", window.l.notification.no_permission);
                    return;
                }
                const id = elem.props.ref.split("::")[0];
                var openedId;
                for (var i = 0; i < tabs.tabs.length; i++) {
                    const tab = tabs.tabs[i];
                    if (elem.props.ref === tab.moduleId) {
                        openedId = tab.id;
                    }
                }
                if (tabs.get(id) === null) {
                    const newTab = {
                        id: id,
                        moduleId: elem.props.ref,
                        text: elem.name,
                        closable: closable,
                        version: version
                    };
                    if (openedId) {
                        CanvasTabManager.insertTab(openedId, newTab);
                    } else {
                        CanvasTabManager.addTab(newTab);
                    }
                }
                else {
                    CanvasTabManager.setTab(id);
                }
                break;
            case "request-zoom-in":
                this.zoomDiagram({}, this.getNextZoomLevel(true));
                break;
            case "request-zoom-out":
                this.zoomDiagram({}, this.getNextZoomLevel(false));
                break;
            case "request-zoom-set":
                const finalZoom = this.getClosestZoomLevel(event.param.zoom / 100);
                this.zoomDiagram({}, finalZoom);
                break;
            case "request-tree-menu":
                if (event.local) {
                    if (event.node && (event.node.type === "variable" || event.node.type === "variables") && !window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule) {
                        contextContainer = $(".context_menu");
                        ReactDOM.render(
                            <ContextMenuWidget
                                type="localTreeNode"
                                context={event.node}
                                domContext={contextContainer}
                                pos={{ left: event.event.pageX, top: event.event.pageY }} />,
                            contextContainer.get(0)
                        );
                    }
                } else {
                    contextContainer = $(".context_menu");
                    ReactDOM.render("",contextContainer.get(0));
                    ReactDOM.render(
                        <ContextMenuWidget
                            type="treeNode"
                            context={event.nodes}
                            domContext={contextContainer}
                            pos={{ left: event.event.pageX, top: event.event.pageY }} />,
                        contextContainer.get(0)
                    );
                }
                break;
            case "request-tree-title-menu":
                contextContainer = $(".context_menu");
                if (event.type === "library") {
                    ReactDOM.render(
                        <ContextMenuWidget
                            type="treeTitle"
                            context="library"
                            domContext={contextContainer}
                            pos={{ left: event.dom.offset().left + event.dom.outerWidth(), top: event.dom.offset().top }} />,
                        contextContainer.get(0)
                    );
                } else {
                    ReactDOM.render(
                        <ContextMenuWidget
                            type="treeTitle"
                            context="member"
                            domContext={contextContainer}
                            pos={{ left: event.dom.offset().left + event.dom.outerWidth(), top: event.dom.offset().top }} />,
                        contextContainer.get(0)
                    );
                }
                break;
            case "request-element-error-message":
                if (event.type === "show") {
                    this.getCanvas().find(".error-bar").data("context", event.context.getID()).html(event.context.getError());
                    this.getCanvas().find(".error-bar-container").show();
                } else if (this.getCanvas().find(".error-bar").data("context")) {
                    if (this.getCanvas().find(".error-bar").data("context") === event.context.getID()) {
                        this.getCanvas().find(".error-bar").data("context", "").html("");
                        this.getCanvas().find(".error-bar-container").hide();
                    }
                } else {
                    this.getCanvas().find(".error-bar").data("context", "").html("");
                    this.getCanvas().find(".error-bar-container").hide();
                }
                break;
            case "request-reload":
                //  CORE-35670: removed modal for cmd+r. simply refreshes page and doesn't call this.reloadBrower
                window.location.reload(true);
                break;
            default:
                console.warn("Unhandled custom event:", event.target);
        }
    }

    /**
     * Handle actions on selection
     * @param {string} op Action
     * @param {Object} event Data payload
     */
    handleSelected(op, event = null) {
        const diagramModel = this.getFlowModel();
        const selection = diagramModel.getSelectedItems();
        const historyLinks = [];
        const historyNodes = [];

        switch (op) {
            case "select-all":
                this.getGraphModel().getEngine().clearRepaintEntities();
                diagramModel.selectAll();
                this.forceUpdate();
                this.getCanvas().focus();
                break;
            case "delete":
                this.clearLinkTerminals();
                this.hidePropertyPanel();
                
                if (!window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule) {
                    for (var i = 0; i < selection.length; i++) {
                        this.handleCustomEvent({target: "request-element-error-message", context: selection[i]})
                        // update serialized.deletedMap
                        const modelType = selection[i].constructor.name;
                        if (modelType.match(/PointModel/)) {
                            window.apm.updateDeletedModulesMap(this.props.node, 'links', selection[i].link.id);
                        }
                        if (modelType.match(/LinkModel/)) {
                            window.apm.updateDeletedModulesMap(this.props.node, 'links', selection[i].id);
                        }
                        if (modelType.match(/NodeModel/)) {
                            window.apm.updateDeletedModulesMap(this.props.node, 'nodes', selection[i].id);
                        }
                        
                        //Prevent updating history with delete even when called from non-keyboard event
                        //This is to prevent having multiple history entries for 1 event
                        if(event !== null) { 
                            if(selection[i].constructor.name === "BaseLinkModel") {
                                const sel = selection[i].id; 
                                
                                historyLinks.push(selection[i]);
                                
                                this.getFlowModel().removeLink(selection[i]);
                                delete selection[i]?.targetPort?.links?.[sel];
                                delete selection[i]?.sourcePort?.links?.[sel];
                            }
                            if(Object.getPrototypeOf(Object.getPrototypeOf(selection[i])).constructor.name === "BaseNodeModel") {    
                                
                                historyNodes.push(selection[i]);
                                
                                this.getFlowModel().removeNode(selection[i]);
                            }
                            if(selection[i].constructor.name === "BasePointModel") {
                                
                                historyLinks.push(selection[i].getLink());
                                
                                this.getFlowModel().removeLink(selection[i].getLink());
                            }
                        }
                        //DEVDES2-1703 - remove() causes problems with links
                        // remove from diagramModel
                        //selection[i].remove();

                    }
                    if(event !== null) {
                        this.history.saveChanges(this.changeTypes.DELETE, { nodes: historyNodes, links: historyLinks });
                    }
                }
                
                this.forceUpdate();
                this.saveDatabase();
                this.handleCustomEvent({ target: "request-update-member" });
                this.getCanvas().focus();
                window.apm.reactDOMComponents.render({ name: 'SettingWidget', props: { serialized: window.apm.serialized }, $el: $('#settings') });

                break;
            case "copy":
                if (selection.length === 0) {
                    break;
                }

                //sorts the selected first to make sure everything else is copied before the links
                const serialized = selection.sort((a, b) => b instanceof DIAG.LinkModel ? -1 : 0).reduce((acc, item) => {                    
                    if (item instanceof DIAG.NodeModel && item.props.nodeTypeName !== "Module Begin") {
                        const previousID = item.id;
                        const id = Utility.uuid4(); 
                        return { ...acc, nodes: [...acc.nodes, { ...item.serialize(), id }], previousIDsMap: { ...acc.previousIDsMap, [previousID]: id } };
                    }
                    if (item instanceof DIAG.LinkModel) {
                        const id = Utility.uuid4(); 
                        const source = acc.previousIDsMap[item.sourcePort.parentNode.id];
                        const target = acc.previousIDsMap[item.targetPort.parentNode.id];
                        return { ...acc, links: [...acc.links, { ...item.serialize(), id, source, target }] };
                    }
                    
                    return acc;
                }, { links: [], nodes: [], previousIDsMap: {} })

                delete serialized.previousIDsMap;

                window.apm.cache.put(Constant.k.NAMESPACE_DATATRANSFER, "clipboard", serialized);
                break;
            case "save":
                const treeDom = $('#tl_container');
                const flow = {
                    offsetX: 0,
                    offsetY: 0,
                    zoom: 100,
                    links: [],
                    nodes: []
                };
                for (var i = 0; i < selection.length; i++) {
                    const element = selection[i];
                    if (element instanceof DIAG.NodeModel) {
                        flow.nodes.push(element.serialize());
                    }
                    if (element instanceof DIAG.LinkModel) {
                        const sl = element.serialize();
                        if (flow.nodes.findIndex(e => e.id === sl.source) !== -1 && flow.nodes.findIndex(e => e.id === sl.target) !== -1) {
                            flow.links.push(sl);
                        }
                    }
                }
                const newNode = {
                    id: Utility.uuid4(),
                    name: event.event.name,
                    type: "module",
                    _class: "ModuleNodeModel",
                    props: {},
                    version: "1",
                    versions: {
                        "1": {
                            version: "1",
                            flow: flow
                        }
                    },
                    needUpdateVariables: true
                };
                window.apm.addNode(newNode);
                treeDom.jstree().create_node("modules", Utility.getTreeNode(newNode), "last", function (new_node) {
                    treeDom.jstree().select_node(new_node);

                    CanvasTabManager.addTab(newNode);

                });
                break;
        }
    }

    /**
     * Get local variables
     * @returns {Array}
     */
    getLocalVariables() {
        if (this.getDataModel()) {
            const localVariables = this.getDataModel().localVariables;
            return localVariables ? localVariables : [];
        }
    }

    /**
     * Search local variables
     * @param {string} query
     * @returns {Array}
     */
    searchVariable(query, reference= null) {
        var r1 = [];
        if (!query) {
            r1 = this.getLocalVariables();
        } else {
            r1 = _.filter(this.getLocalVariables(), node => {
                return VariableDataModel.match(query, node.props.config);
            });
        }
        var r2 = [];
        for (var i = 0; i < r1.length; i++) {
            if (reference === "moduleReference") { if (r1[i].props.config.type === "MODULE") r2.push(new VariableDataModel(r1[i].props)); }
            else if (reference==="promptReference") { if (r1[i].props.config.type === "PROMPT") r2.push(new VariableDataModel(r1[i].props)); }
            else { r2.push(new VariableDataModel(r1[i].props)); }
        }
        return r2;
    }


    /**
     * Get the closest grid point to event position
     * @param {Object} event React mouse event, or mocked event
     * @param {Object} points Normalized event coordinate
     * @returns {Object}
     */
    getClosestGridPoint(event, points) {
        if (typeof points === 'undefined') {
            points = this.getGraphModel().getEngine().getRelativeMousePoint(event);
        }

        var amountX = Math.round(points.x / this.getDefaultGridSize()) * this.getDefaultGridSize();
        var amountY = Math.round(points.y / this.getDefaultGridSize()) * this.getDefaultGridSize();

        while (this.getFlowModel().posHasOverlap({ x: amountX, y: amountY })) {
            amountX += this.getDefaultGridSize();
            amountY += this.getDefaultGridSize();
        }

        return {
            x: this.getFlowModel().getGridPosition(amountX),
            y: this.getFlowModel().getGridPosition(amountY)
        };
    }

    /**
     * Get the zoom level in bracket closest to given custom zoom level
     * @param {number} zoom Zoom level, in term of number scales
     * @returns {number}
     */
    getClosestZoomLevel(zoom) {
        var finalZoom = zoom;
        if (zoom <= 0) {
            finalZoom = 1;
        } else if (zoom > this.zoomBrackets[this.zoomBrackets.length - 1]) {
            finalZoom = this.zoomBrackets[this.zoomBrackets.length - 1];
        } else {
            const newGridSize = Math.round(this.getDefaultGridSize() * zoom);
            finalZoom = Math.min(Math.max(this.zoomBrackets[1], newGridSize / this.getDefaultGridSize()), this.zoomBrackets[this.zoomBrackets.length - 1]);
        }
        return finalZoom;
    }

    /**
     * Get the next zoom level in bracket given current zoom level
     * @param {boolean} isIncrement If true, get the next larger zoom level; otherwise, get the next smaller zoom level
     * @returns {number}
     */
    getNextZoomLevel(isIncrement) {
        const zoomLevel = this.getFlowModel().getZoomLevel() / 100;
        for (var i = 1; i < this.zoomBrackets.length; i++) {
            if (zoomLevel === this.zoomBrackets[i]) {
                if (isIncrement) {
                    return this.zoomBrackets[Math.min(this.zoomBrackets.length - 1, i + 1)];
                } else {
                    return this.zoomBrackets[Math.max(1, i - 1)];
                }
            } else if (zoomLevel > this.zoomBrackets[i - 1] && zoomLevel < this.zoomBrackets[i]) {
                if (isIncrement) {
                    return this.zoomBrackets[i];
                } else {
                    return this.zoomBrackets[Math.max(1, i - 1)];
                }
            }
        }
        return 1;
    }

    /**
     * Get canvas reference
     * @returns {jQuery}
     */
    getCanvas() {
        if (!this.canvas || !this.canvas.length) {
            this.canvas = $(".id-" + this.props.clientId);
        }
        return this.canvas;
    }
    
    /**
     * Adjusts canvas offset so that property panel does not overlap a selected node
     * @param {number} clientX 
     */
    setCanvasOffsetForPropertyPanel(clientX){
        const canvasWidth = this.getCanvas().width();
        const canvasOffsetX = this.getGraphModel().graphModel.getOffsetX();
        const propertyPanelWidth = 300;  // hard-coded width of property panel plus padding
        const targetX = canvasWidth - propertyPanelWidth;
        const nodeWidth = 180; // hard-coded with of canvas nodes

        this.getGraphModel().graphModel.setOffsetX(canvasOffsetX - (clientX - targetX + nodeWidth));

    }

    /**
     * Adjusts canvas offset so centroid coordinates are center screen
     */
     setCanvasCenter(){
        this.zoomDiagram({}, 1);
        const widthOffset = this.getCanvas().width() / 2;
        const heightOffset = this.getCanvas().height() / 2;
        let offsetXSum = 0;
        let offsetYSum = 0;

        const nodes = Object.values(this.getGraphModel().graphModel.nodes);
        nodes.forEach(node => {
            offsetXSum = offsetXSum + node.x;
            offsetYSum = offsetYSum + node.y;
        });

        const xCenter = offsetXSum / nodes.length;
        const yCenter = offsetYSum / nodes.length;

        const xCoordinate = -xCenter + widthOffset;
        const yCoordinate = -yCenter + heightOffset - 100;

        this.getGraphModel().graphModel.setOffset(xCoordinate,yCoordinate);
        this.handleCustomEvent({ target: "request-refresh" });
        this.saveDatabase();
    }

    /**
     * Determines if the property panel will occlude a selected canvas node
     * @param {number} clientX 
     * @returns Boolean
     */
    shouldOffsetCanvasForPropertyPanel(clientX){
        return this.getCanvas().width() - clientX <= 300;
    }

    /**
     * Get canvas engine
     */
    getEngine() {
        return this.getGraphModel().getEngine();
    }

    /**
     * Get default grid size
     * @returns {number}
     */
    getDefaultGridSize() {
        if (this.canvasDefaultGridSize === 0) {
            const bgSize = this.getCanvas().css('background-size');
            this.canvasDefaultGridSize = parseFloat(bgSize.substr(0, bgSize.search("px")));
        }
        return this.canvasDefaultGridSize;
    }

    /**
     * Change zoom level of canvas
     * @param {Object} event React event or mocked event
     * @param {number} forceZoomFactor If greater than 0, force the new zoom level to be this value
     * @param {boolean} resetOffset If true, reset offset to (0, 0)
     * @param {Object|null} forceOffset
     */
    zoomDiagram(event, forceZoomFactor = 0, resetOffset = false, forceOffset = null) {
        const diagramEngine = this.getGraphModel().getEngine();
        const diagramModel = diagramEngine.getDiagramModel();
        const oldZoomFactor = diagramModel.getZoomLevel() / 100;
        let scrollDelta = this.props.inverseZoom ? -event.deltaY : event.deltaY;
        var zoomOut = scrollDelta < 0;
        var bgSize = this.getCanvas().css('background-size');
        var gridSize = parseFloat(bgSize.substr(0, bgSize.search("px")));
        var newGridSize = zoomOut ? gridSize - 1 : gridSize + 1;
        if (forceZoomFactor > 0) {
            newGridSize = Math.round(this.getDefaultGridSize() * forceZoomFactor);
            zoomOut = forceZoomFactor < oldZoomFactor;
        }
        if (newGridSize < 5) {
            newGridSize = 5;
        }
        if (newGridSize > 5 * this.getDefaultGridSize()) {
            newGridSize = 5 * this.getDefaultGridSize();
        }
        // Get zoom level based on grid size
        const zoomFactor = zoomOut ? newGridSize / this.getDefaultGridSize() : newGridSize / this.getDefaultGridSize();
        // Hide grid if zoomed way out, since grid will be too small to see
        if (newGridSize < 5) {
            this.getCanvas().addClass("clean-background");
        } else if (this.getCanvas().hasClass("clean-background")) {
            this.getCanvas().removeClass("clean-background");
        }
        // Dynamically change grid width for more legible grids
        if (zoomFactor < 2) {
            const gridWidth = 100 - Math.floor(1 / newGridSize * 100);
            this.getCanvas().css(
                "background-image",
                "linear-gradient(0deg, transparent, transparent " + gridWidth + "%," +
                "rgba(4, 52, 133, 0.1) " + gridWidth + "%, rgba(4, 52, 133, 0.1))," +
                "linear-gradient(90deg, transparent, transparent " + gridWidth + "%," +
                "rgba(4, 52, 133, 0.1) " + gridWidth + "%, rgba(4, 52, 133, 0.1))"
            );
        }
        // Update zoom level
        diagramModel.setZoomLevel(zoomFactor * 100);
        if (resetOffset) {
            diagramModel.setOffset(0, 0);
            if (forceZoomFactor > 0 && forceOffset) {
                diagramModel.setOffset(forceOffset.x, forceOffset.y);
            }
        } else {
            // Set graph offset based on new zoom level
            var boundingRect;
            if (!event.hasOwnProperty("currentTarget")) {
                boundingRect = {
                    width: this.getCanvas().width(),
                    height: this.getCanvas().height(),
                    top: this.getCanvas().position().top,
                    left: this.getCanvas().position().left
                };
            } else {
                boundingRect = event.currentTarget.getBoundingClientRect();
            }
            const clientWidth = boundingRect.width;
            const clientHeight = boundingRect.height;
            // compute difference between rect before and after scroll
            const zoomDiff = zoomFactor - oldZoomFactor;
            const widthDiff = clientWidth * zoomDiff;
            const heightDiff = clientHeight * zoomDiff;
            // compute mouse coords relative to canvas
            var clientX, clientY;
            if (forceZoomFactor > 0) {
                clientX = boundingRect.left + boundingRect.width / 2;
                clientY = boundingRect.top + boundingRect.height / 2;
            } else {
                clientX = event.clientX - boundingRect.left;
                clientY = event.clientY - boundingRect.top;
            }
            // compute width and height increment factor
            const xFactor = (clientX - diagramModel.getOffsetX()) / oldZoomFactor / clientWidth;
            const yFactor = (clientY - diagramModel.getOffsetY()) / oldZoomFactor / clientHeight;
            diagramModel.setOffset(
                diagramModel.getOffsetX() - widthDiff * xFactor,
                diagramModel.getOffsetY() - heightDiff * yFactor
            );
        }
        this.getCanvas().css({
            "background-size": newGridSize + "px " + newGridSize + "px",
            "background-position-x": diagramModel.getOffsetX() + "px",
            "background-position-y": diagramModel.getOffsetY() + "px"
        });

        // Block nodes from refreshing; only refresh the canvas itself
        diagramEngine.enableRepaintEntities([]);
        this.forceUpdate();
        this.getCanvas().children(".link-terminal-view").css("transform", "translate(" + diagramModel.getOffsetX() + "px," + diagramModel.getOffsetY() + "px) scale(" + zoomFactor + ")");
        $("#value-zoom").val(Math.round(this.getFlowModel().getZoomLevel()) + "%");
        document.dispatchEvent(new CustomEvent('ev-canvas-zoom-change', { detail: { zoom: zoomFactor, cliendId: this.props.clientId } }));
    }

    /**
     * Show terminal controls for re-wiring a link
     * @param {Object} link Link model
     */
    showLinkTerminals(link) {
        this.clearLinkTerminals();
        this.currentHighlightedLink = link;
        this.getFlowModel().clearSelection();
        const that = this;
        var previousSource = null, previousTarget = null;
        if (link.getSourcePort()) {
            const oSPort = link.getSourcePort();
            const pts = link.getPointForPort(link.getSourcePort());
            const p0 = $("<div class='link-terminal point link-" + link.getID() + "'></div>")
                .attr('data-linkid', link.id)
                .attr('data-id', pts.id);
            p0.css({
                top: pts.y,
                left: pts.x,
                display: "block"
            }).on('mousedown', function (e) {
                // Pre-detach link from port to enable moving
                $(this).data('down', true);
                previousSource = link.getSourcePort();
                link.setSourcePort(null);
                updateLastChange(link.props);
                that.linkDetached = true;
            }).on('mouseleave', function (e) {
                if ($(this).data('down')) {
                    // Remove link connection only when moved away far enough
                    link.setPreviousPort("source", previousSource);
                    // Remove self so that event listeners are gone
                    $(this).remove();
                }
            }).on('mouseup', function (e) {
                // Re-connect since it's not moved away far enough
                link.setSourcePort(oSPort);
                that.linkDetached = false;
            });
            this.getCanvas().children(".link-terminal-view").append(p0);
            p0.animateCss("bounceIn");
        }
        if (link.getTargetPort()) {
            const oTPort = link.getTargetPort();
            const ptt = link.getPointForPort(link.getTargetPort());
            const p1 = $("<div class='link-terminal point link-" + link.getID() + "'></div>")
                .attr('data-linkid', link.id)
                .attr('data-id', ptt.id);
            p1.css({
                top: ptt.y,
                left: ptt.x,
                display: "block"
            }).on('mousedown', function (e) {
                // Pre-detach link from port to enable moving
                $(this).data('down', true);
                previousTarget = link.getTargetPort();
                link.setTargetPort(null);
                updateLastChange(link.props);
                that.linkDetached = true;
            }).on('mouseleave', function (e) {
                if ($(this).data('down')) {
                    // Remove link connection only when moved away far enough
                    link.setPreviousPort("target", previousTarget);
                    // Remove self so that event listeners are gone
                    $(this).remove();
                }
            }).on('mouseup', function (e) {
                // Re-connect since it's not moved away far enough
                link.setTargetPort(oTPort);
                that.linkDetached = false;
            });
            this.getCanvas().children(".link-terminal-view").append(p1);
            p1.animateCss("bounceIn");
        }
    }

    /**
     * Handle download image request to generate an image of the active flow and download it
     * @param {String} filename Filename to use for the image
    */
    requestDownloadImageHandler(filename) {
        Utility.openApplicationOverlay(`Generating image...`);
        const wrapperId = "canvas-image-wrapper-" + Utility.uuid4();
        const domCanvas = this.getCanvas();
        //this an extra margin to make sure items to the left or top don't sit too close to the edge of the image.
        const extraMarginValue = 20;
        //this is the extra margin we want to add to the right and bottom of the image to make sure it doesn't get cut off since the size of the element is unknown and the expansion is to the right and bottom
        const bootomRightMargin = 500;
        const valuesForX = [];
        const valuesForY = [];
        const currentFlow = this.getDataModel().versions[this.getDataModel().version].flow;

        for (let i = 0; i < currentFlow.nodes.length; i++) {
            const element = currentFlow.nodes[i];
            valuesForX.push(element.x);
            valuesForY.push(element.y);
        }

        const minX = Math.min(...valuesForX, 0);
        const minY = Math.min(...valuesForY, 0);
        const shouldAddX = minX < 0 ? extraMarginValue: 0;
        const shouldAddY = minY < 0 ? extraMarginValue: 0;
        //if the min value is negative, we need to add it and the margin value to the max value to make sure the image is not cut off for not having enough space
        const maxWidth = (shouldAddX ? Math.abs(minX) + shouldAddX: 0) + Math.max(...valuesForX) + bootomRightMargin;
        const maxHeight = (shouldAddY ? Math.abs(minY) + shouldAddY: 0) + Math.max(...valuesForY) + bootomRightMargin;

        this.zoomDiagram({}, 1, true, { x: -minX + shouldAddX , y: -minY + shouldAddY });
        domCanvas.css({ 
            width: maxWidth + "px",
            height: maxHeight + "px", 
        });

        //this block is to wrap the canvas in a div so that the image doesn't have and extra white space around it
        const wrapper = document.createElement('div');
        wrapper.id = wrapperId;
        const domToConvert = domCanvas[0];
        const parent = domToConvert.parentNode;
        parent.replaceChild(wrapper, domToConvert);
        wrapper.appendChild(domToConvert);

        DomToImage.toBlob(wrapper)
            .then(function (blob) {
                FileSaver.saveAs(blob, filename + '_image.png');
                domCanvas.css({ width: "auto", height: "100vh" });
                window.apm.getCurrentDiagramWidget().setCanvasCenter(); 
                // this is to unwrap the canvas from the new div so the app doesn't add extra divs every time the user downloads an image
                $(`#${wrapperId}`).contents().unwrap();    
            }).catch(function (error) {
                console.error('something went wrong downloading the image!', error);
            }).finally(function(){
                Utility.closeApplicationOverlay();
            });
    }
    
    /**
     * Hide terminal controls for re-wiring a link
     */
    clearLinkTerminals() {
        this.getCanvas().find(".link-terminal").remove();
    }

    /**
     * Callback for member tree
     */
    // TODO:
    openNewLocalVariableDialog(config = {}) {
        const isLocalVar = config.isLocalVar || false;
        const useModule = config.useModule || true;

        window.apm.popup(window.l.popup.new_local_variable,
            '<div class="new_element_container"></div>',
            '<button class="w2ui-btn secondary close-button">' + window.l.popup.close + '</button> ' +
            '<button class="w2ui-btn primary add-button">Add Local Variable</button> ',
            [
                ".new_element_container",
                () => {
                    ReactDOM.render(<NewLocalVariableDialog 
                            isLocalVar={isLocalVar} 
                            useModule={useModule} />, $(".new_element_container").get(0));
                    $(".w2ui-popup").find(".add-button").on("click", function () {
                        document.dispatchEvent(new CustomEvent("ev-dialog-add"));
                    });
                    $(".w2ui-popup").find(".close-button").on("click", function () {
                        w2popup.close();
                        ReactDOM.unmountComponentAtNode($(".new_element_container").get(0));
                    });
                
                }
            ]
            
        );
    }

    /**
     * Show property panel for a given model
     * @param {Object} model Node or link model
     * @param {number} eventX Mouse click event x coordinate
     * @param {boolean} toolbarNode launched from toolbar (drag drop to canvas)
     */
    showPropertyPanel(model, eventX = null, toolBarNode = false) {
        const design = window.apm.serialized;
        const { features } = design.rootSettings;
        if ($("#dialogs").is(":visible")) {
            return;
        } else if (
            features.intentManagement
            && model instanceof IntentDataModel
        ) {
            // Display intent details instead of property panel.
            this.hidePropertyPanel();
            const intent = design.components.intents.find(({ id }) => {
                return id === model.id;
            });
            if (!intent) {
                errorMessageService.addError(new Error(
                    `Unable to find intent with ID '${model.id}'`
                ));
                return;
            }

            globalModalsService.activeDialog = (
                <IntentDetails
                    intent={intent}
                    mode={IntentDetailsMode.edit}
                    onComplete={(updatedIntent) => {
                        if (updatedIntent) {
                          Object.assign(intent, updatedIntent);
                          window.apm.setSerializedChanged();
                          window.apm.refreshApplicationTree();
                        }
                        globalModalsService.activeDialog = null;
                    }}
                />
            );
            return;
        }

        const diagramEngine = this.getGraphModel().getEngine();
        const diagramModel = diagramEngine.getDiagramModel();
        const selectedItems = diagramModel.getSelectedItems().filter(item => !!(item instanceof DIAG.NodeModel))

        if(selectedItems.length > 1) {
            this.hidePropertyPanel();
            return;
        }
        
        const panel = $("#property_panel");
        const node = this.props.node;
        const displayClass = (model.hasWriteProxy && model.hasWriteProxy()) ? "app-component" : ''; 
        const canvasWidth = this.getCanvas().width();
        
        this.setState({selectedContext: model}, () => {
            ReactDOM.render(
                <PropertyPanelWidget 
                    context={this.state.selectedContext}
                    panelDOM={panel}
                    displayClass={displayClass}
                    embed={false} 
                    node={node}
                    eventX={eventX}
                    canvasWidth={canvasWidth}
                    toolBarNode={toolBarNode}
                />, panel.get(0)
            );
        });
                    
    }

    updateVariables() {
        const diagramEngine = this.getGraphModel().getEngine();
        const diagramModel = diagramEngine.getDiagramModel();
        const nodes = Object.values(diagramModel.getNodes());
        for (var i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            node.updateVariables();
        }
        const links = Object.values(diagramModel.getLinks());
        for (var i = 0; i < links.length; i++) {
            const link = links[i];
            link.updateVariables();
        }
    }

    updateFocus() {
        $("#value-zoom").val(Math.round(this.getFlowModel().getZoomLevel()) + "%");
        this.handleCustomEvent({ target: "request-update-variables" });
        this.handleCustomEvent({ target: "request-update-language-setting" });
        this.handleCustomEvent({ target: "request-update-member" });
        this.handleCustomEvent({ target: "request-update-property" });
    }

    configEvaluation() {
        var argInputs;
        if (this.getDataModel()) {
            if (this.getDataModel().elemId) {
                const idFields = this.getDataModel().elemId.split("_");
                const node = window.apm.getNode(idFields[0]);
                const elemIndex = node.versions[node.version].flow.nodes.findIndex(element => element.id === idFields[1]);
                const elem = node.versions[node.version].flow.nodes[elemIndex];
                if (elem.props.config) {
                    argInputs = elem.props.config.inputs;
                }
            } else {
                const node = this.getDataModel();
                if (node.props.config) {
                    argInputs = node.props.config.inputs;
                }
            }
            if (!argInputs) {
                return;
            }
            var staticInputs = [];
            for (var i = 0; i < argInputs.length; i++) {
                if (argInputs[i].type === "value") {
                    staticInputs.push({
                        varName: argInputs[i].argumentName,
                        value: argInputs[i].value
                    });
                }
            }
            this.getFlowModel().configEvaluation(staticInputs);
        }
    }

    /**
     * Hide property panel
     */
    hidePropertyPanel() {
        Utility.hidePropertyPanel();
        this.setState({selectedContext:null});
    }

    /**
     * Add view dropdown upon mount
     */
    componentDidMount() {
        this.getGraphModel().getEngine().enableRepaintEntities([]);
        // keyboard events
        window.addEventListener("keydown", this.handleKeyDown.bind(this));
        this.onKeyUpPointer = this.onKeyUp.bind(this);
        this.setState({
            document: document,
            renderedNodes: true,
            diagramEngineListener: this.getGraphModel().getEngine().addListener({
                repaintCanvas: () => {
                    this.forceUpdate();
                }
            })
        });
        window.addEventListener("keyup", this.onKeyUpPointer, false);
        // dont focus the window when in test mode - jsdom fails
        if (process.env.NODE_ENV !== "test") {
            window.focus();
        }
        // visually indicate if viewing a common module
        const isCommonModuleCanvas = this.props.node.props.isCommonModule || Utility.getIsCommonModule(window.apm.serialized);
        if(isCommonModuleCanvas){
            this.getCanvas().addClass('common-module');
        }
        this.getCanvas().focus();
        
        const languages = window.apm.knowledgeBase.getLanguages();
        var langIdx = languages.findIndex(element => element.value === getGlobalSettings().language);
        if (langIdx === -1) {
            langIdx = 0;
        }
        
        this.configEvaluation();
        this.updateCanvas();
    }

    updateCanvas() {
        const ver = this.getDataModel().versions[this.getDataModel().version];
        if (ver) {
            const flow = ver.flow;
            const zoomLevel = flow.zoom ? flow.zoom / 100 : 1;
            const offsetX = flow.offsetX ? flow.offsetX : 0;
            const offsetY = flow.offsetY ? flow.offsetY : 0;
            this.zoomDiagram({ deltaY: 0 }, zoomLevel, true, { x: offsetX, y: offsetY });
        }
    }

    componentWillUpdate(nextProps) { }

    componentWillUnmount() {
        window.removeEventListener("keydown", this.handleKeyDown);
    }

    /**
     * Gets a model and element under the mouse cursor
     */
    getMouseElement(event) {
        var target = event.target;
        var diagramModel = this.getFlowModel();
        //is it a port
        var element = DIAG.Toolkit.closest(target, ".port[data-name]");
        if (element) {
            var nodeElement = DIAG.Toolkit.closest(target, ".node[data-nodeid]");
            return {
                model: diagramModel.getNode(nodeElement.getAttribute("data-nodeid")).getPort(element.getAttribute("data-name")),
                element: element
            };
        }
        //look for a point
        element = DIAG.Toolkit.closest(target, ".point[data-id]");
        if (element) {
            return {
                model: diagramModel.getLink(element.getAttribute("data-linkid")).getPointModel(element.getAttribute("data-id")),
                element: element
            };
        }
        //look for a link
        element = DIAG.Toolkit.closest(target, "[data-linkid]");
        if (element) {
            return {
                model: diagramModel.getLink(element.getAttribute("data-linkid")),
                element: element
            };
        }
        //look for a node
        element = DIAG.Toolkit.closest(target, ".node[data-nodeid]");
        if (element) {
            return {
                model: diagramModel.getNode(element.getAttribute("data-nodeid")),
                element: element
            };
        }
        return null;
    }


    /**
     * Mouse move handler
     * @param {Object} event React event
     */
    onMouseMove(event) {
        const diagramEngine = this.getGraphModel().getEngine();
        const diagramModel = diagramEngine.getDiagramModel();
        // disable dragging in common modules
        if (window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule && this.mouseDrag.active) {
            this.stopFiringAction();
        }
        //select items so draw a bounding box
        if (this.state.action instanceof DIAG.SelectingAction) {
            const relative = diagramEngine.getRelativePoint(event.clientX, event.clientY);
            const nodes = Object.values(diagramModel.getNodes());
            for (var i = 0; i < nodes.length; i++) {
                const node = nodes[i];
                if (this.state.action.containsElement(node.x, node.y, diagramModel)) {
                    node.setSelected(true);
                }
            }
            const links = Object.values(diagramModel.getLinks());
            for (var i = 0; i < links.length; i++) {
                const link = links[i];
                var allSelected = true;
                for (var j = 0; j < link.points.length; j++) {
                    const point = link.points[j];
                    if (this.state.action.containsElement(point.x, point.y, diagramModel)) {
                        point.setSelected(true);
                    } else {
                        allSelected = false;
                    }
                }
                if (allSelected) {
                    link.setSelected(true);
                }
            }
            this.state.action.mouseX2 = relative.x;
            this.state.action.mouseY2 = relative.y;
            // Refresh only those who are affected
            diagramEngine.enableRepaintEntities(diagramModel.getSelectedItems());
            this.fireAction();
            this.setState({ action: this.state.action });
        } else if (this.state.action instanceof DIAG.MoveItemsAction) {

            this.handleMoveSelected(event, diagramModel);
            this.fireAction();
            this.forceUpdate();
        } else if (this.state.action instanceof DIAG.MoveCanvasAction) {
            // Move canvas only; disable refreshing on nodes
            diagramEngine.enableRepaintEntities([]);
            //translate the actual canvas
            if (this.props.allowCanvasTranslation) {
                diagramModel.setOffset(
                    this.state.action.initialOffsetX + (event.clientX - this.state.action.mouseX),
                    this.state.action.initialOffsetY + (event.clientY - this.state.action.mouseY)
                );
                this.fireAction();
                this.forceUpdate();
            }
        }
    }

    /**
     * keydown event handler
     * @param {Object} evt 
     */
    handleKeyDown(evt) {
        const diagramEngine = this.getGraphModel().getEngine();
        const diagramModel = diagramEngine.getDiagramModel();
        const keyCodes = [37, 38, 39, 40];
        const isTargetDiagram = evt.target.classList.value.match(/storm-diagrams-canvas/);

        // only handle selections in current diagram
        function getCurrentModuleSelections(selections) {
            if (diagramModel.id === window.apm.serialized.currentActiveTab) {
                return selections;
            }
            return [];
        }
        if (isTargetDiagram && getCurrentModuleSelections(diagramModel.getSelectedItems()).length > 0 && keyCodes.indexOf(evt.keyCode) > -1) {
            let newX = 0;
            let newY = 0;
            const shiftKey = evt.shiftKey;
            const amountZoom = diagramModel.getZoomLevel() / 100;
            const module = window.apm.serialized.components.modules.find(mod => mod.id === diagramModel.id);

            if (evt.keyCode === 37) {
                newX = -1;
                if (shiftKey) {
                    newX = newX - 10;
                }
            }

            if (evt.keyCode === 39) {
                newX = 1;
                if (shiftKey) {
                    newX = newX + 10;
                }
            }

            if (evt.keyCode === 38) {
                newY = -1;
                if (shiftKey) {
                    newY = newY - 10;
                }
            }
            if (evt.keyCode === 40) {
                newY = 1;
                if (shiftKey) {
                    newY = newY + 10;
                }
            }

            for (var i = 0; i < diagramModel.getSelectedItems().length; i++) {
                var model = diagramModel.getSelectedItems()[i];
                const { x, y } = model;

                if (model instanceof DIAG.NodeModel) {
                    // Item is a node
                    model.x = diagramModel.getGridPosition(model.x + (newX / amountZoom));
                    model.y = diagramModel.getGridPosition(model.y + (newY / amountZoom));
                    if (this.getFlowModel().nodeHasOverlap(model)) {
                        model.x += this.getDefaultGridSize();
                        model.y += this.getDefaultGridSize();
                    }

                    // if coords have changed, update corresponding node's props.lastLocalChange
                    if (model.x !== x || model.y !== y) {
                        const node = module.versions[1].flow.nodes.find(n => n.id === model.id);
                        if (node) {
                            updateLastChange(node.props);
                        }
                    }
                }

                if (model instanceof DIAG.PointModel) {
                    // Item is a point bind to a node port
                    if (!model.isConnectedToPort()) {
                        model.x = diagramModel.getGridPosition(model.x + (newX / amountZoom));
                        model.y = diagramModel.getGridPosition(model.y + (newY / amountZoom));
                    } else {
                        model.x = model.x + diagramModel.getGridPosition(1 / amountZoom);
                        model.y = model.y + diagramModel.getGridPosition(1 / amountZoom);
                    }

                    // if coords have changed, update corresponding link's props.lastLocalChange
                    if (model.x !== x || model.y !== y) {
                        const link = module.versions[1].flow.links.find(l => l.id === model.link.id);
                        if (link) {
                            updateLastChange(link.props);
                        }
                    }
                }

                this.fireAction();
                this.forceUpdate();
            }
        }
    }

    handleMoveSelected(event, diagramModel) {
        let amountX = event.clientX - this.state.action.mouseX;
        let amountY = event.clientY - this.state.action.mouseY;
        let amountZoom = diagramModel.getZoomLevel() / 100;
        const zoomByGridSize = this.getDefaultGridSize() * amountZoom;

        for (var i = 0; i < this.state.action.selectionModels.length; i++) {
            var model = this.state.action.selectionModels[i];
            // in this case we need to also work out the relative grid position
            if (model.model instanceof DIAG.NodeModel) {
                // Item is a node
                amountX = Math.round(amountX / zoomByGridSize) * zoomByGridSize;
                amountY = Math.round(amountY / zoomByGridSize) * zoomByGridSize;

                model.model.x = diagramModel.getGridPosition(model.initialX + amountX / amountZoom);
                model.model.y = diagramModel.getGridPosition(model.initialY + amountY / amountZoom);

                if (this.getFlowModel().nodeHasOverlap(model.model)) {
                    model.model.x += this.getDefaultGridSize();
                    model.model.y += this.getDefaultGridSize();
                }
            } else if (model.model instanceof DIAG.PointModel && !model.model.isConnectedToPort()) {
                // Item is a free-floating point on a link
                model.model.x = diagramModel.getGridPosition(model.initialX + amountX / amountZoom);
                model.model.y = diagramModel.getGridPosition(model.initialY + amountY / amountZoom);
            } else if (model.model instanceof DIAG.PointModel) {
                // Item is a point bind to a node port
                model.model.x = model.initialX + diagramModel.getGridPosition(amountX / amountZoom);
                model.model.y = model.initialY + diagramModel.getGridPosition(amountY / amountZoom);
            }
        }
    }

    /**
     * Mouse up handler
     * @param {Object} event React event
     */
    onMouseUp(event) {

        if (event.button === 1) {
            this.getCanvas().css("cursor", "default");
        }
        if (window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule && this.mouseDrag.active) {
            this.stopFiringAction();
        }
        const diagramEngine = this.getGraphModel().getEngine();
        //are we going to connect a link to something?
        if (this.state.action instanceof DIAG.MoveItemsAction) {
            var element = this.getMouseElement(event);
            if (!element) {
                return;
            }
            var hasNonPoint = false;
            var linkRemoved = false;

            const nodeModelCount = this.state.action.selectionModels.filter((model) => {
                return model.model instanceof DIAG.NodeModel;
            }).length;
            const pointModelCount = this.state.action.selectionModels.filter((model) => {
                return (
                    model
                    && model.model instanceof DIAG.PointModel
                    && !(element.model instanceof DIAG.PortModel)
                    && !(element.model instanceof DIAG.NodeModel)
                );
            }).length;

            const historyModels = [];
            const historyPointModels = [];

            for (var i = 0; i < this.state.action.selectionModels.length; i++) {
                var model = this.state.action.selectionModels[i];

                if (model.model instanceof DIAG.NodeModel) {
                    const { initialX, initialY } = model;
                    const { x, y } = model.model;
                    const previousTimestamp = model.model.props.lastLocalChange;

                    if (initialX !== x || initialY !== y) {
                        const positionObject = {initial: {x:initialX,y:initialY}, 
                                                new: {x:x,y:y}, 
                                                nodeModel: model.model
                                                };
                        
                        historyModels.push(positionObject);
                        this.getFlowModel().updateNodePos(model.model);
                        //not sure why there is a break here
                        //break;
                        //only add the history changes object when the models array is filled
                        if(historyModels.length === nodeModelCount) {
                            this.history.saveChanges(this.changeTypes.MOVED_POSITION, {models: historyModels, previousTimestamp: previousTimestamp});
                        }
                    }
                    // dont select points/links
                    hasNonPoint = true;
                }
            }
            for (var i = 0; i < this.state.action.selectionModels.length; i++) {
                var model = this.state.action.selectionModels[i];
                //only care about points connecting to things
                if (!(model.model instanceof DIAG.PointModel)) {
                    continue;
                }
                // Item is a point, target element is a port, and the selection contains at least a node
                if (
                    !hasNonPoint && 
                    model.model instanceof DIAG.PointModel &&
                    element && 
                    element.model instanceof DIAG.PortModel && 
                    !diagramEngine.isModelLocked(element.model)
                ) {
                    var link = model.model.getLink();
                    linkRemoved = false;

                    //DEVDES2-1163: isSamePortParentId now checks if model.model.link.sourcePort is undefined

                    const isSamePortParentId = model.model.link.sourcePort && model.model.link.sourcePort.parentNode.id === element.model.parentNode.id;

                    // DEVDES2-1072: Fixes "Node becomes undraggable if you try to move it"
                    if(isSamePortParentId) {
                        linkRemoved = true;
                    }
                    //DEVDES2-135 don't allow link from Module Exit Node
                    const sourceNode = model.model.link.getSourceNode();
                        if ((sourceNode!==null && sourceNode.props.nodeTypeName==="Module Exit")||(model.model.link.targetPort!==null&&element.model.parentNode&&element.model.parentNode.props.nodeTypeName==="Module Exit")) {
                            model.model.link.remove();
                            window.apm.notification.post("error", window.l.notification.cannot_have_transition_from_exit_node);
                        }
                    if (link.isLastPoint(model.model)) {
                        const source = link.getSourcePort();
                        let historyLinks = [];

                        if (source && source.getID() === element.model.getID()) {
                            linkRemoved = true;
                        }
                        if (!linkRemoved) {
                            if(model.model.link.previousPorts.target) {
                                historyModels.push(model.model.link.previousPorts.target);
                                historyModels.push(element.model);
                            }
                            const previousTimestamp = link.props.lastLocalChange;

                            link.setTargetPort(element.model);
                            updateLastChange(link.props);
                            
                            historyLinks.push(link);
                            
                            this.history.saveChanges(this.changeTypes.NEW_LINK_TARGET, { links: historyLinks, models: historyModels, previousTimestamp: previousTimestamp });

                        }
                    } else if (link.getPointIndex(model.model) === 0) {
                        const target = link.getTargetPort();
                        let historyLinks = [];
                        if (target && target.getID() === element.model.getID()) {
                            linkRemoved = true;
                        }
                        if (!linkRemoved) {
                            const previousTimestamp = link.props.lastLocalChange;
                            
                            historyModels.push(model.model.link.previousPorts.source);
                            historyModels.push(element.model);

                            link.setSourcePort(element.model);
                            updateLastChange(link.props);
                            
                            historyLinks.push(link);
                            
                            this.history.saveChanges(this.changeTypes.NEW_LINK_SOURCE, { links: historyLinks, models: historyModels, previousTimestamp: previousTimestamp });

                        }
                    }
                    if (!linkRemoved) {
                        link.setSelected(true);
                        link.checkSourceOutDegree();
                        this.showPropertyPanel(model.model.link, event.clientX, true);
                        // Focus the property panel input when a link is created and connected
                        Utility.focusPropertyPanelInput();
                        delete this.getGraphModel().getEngine().linksThatHaveInitiallyRendered[link.getID()];
                    } else {
                        link.remove();
                    }
                }
                // Item is a point
                if (model.model instanceof DIAG.PointModel) {

                    //Add point movement to change history
                    if(!(element.model instanceof DIAG.PortModel) && !(element.model instanceof DIAG.NodeModel)) {
                        
                        const { initialX, initialY } = model;
                        const { x, y } = model.model;
                        const previousTimestamp = model.model.link.props.lastLocalChange;

                        let positionObject = {initial: {x:initialX,y:initialY}, 
                                              new: {x:x,y:y}, 
                                              nodeModel: model.model
                                            };

                        if (initialX !== x || initialY !== y) {

                            historyPointModels.push(positionObject);
                        
                        }

                        if (model.model.link instanceof BaseLinkModel) {
                            updateLastChange(model.model.link.props);
                        }

                        if(historyPointModels.length === pointModelCount) {

                            this.history.saveChanges(this.changeTypes.MOVED_POINT_POSITION, {models: historyPointModels, previousTimestamp: previousTimestamp});

                        }
                    }
                    const link = model.model.getLink();
                    linkRemoved = false;
                    if (!link.getSourcePort() || !link.getTargetPort()) {
                        //DEVDES2-1244 revert this feature which was fixed in 135 ticket
                        // // remove if there is no targetport
                        // const isTargetPortNull = model.model.link && model.model.link.targetPort === null;
                        // if (isTargetPortNull) {
                        //     model.model.link.remove();
                        // }
                        
                        const p1 = link.getFirstPoint();
                        const p2 = link.getLastPoint();
                        if (400 > Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2)) {
                            link.remove();
                            linkRemoved = true;
                        }
                    }
                    if (!linkRemoved) {
                        if (!hasNonPoint) {
                            link.setSelected(true);
                        }
                        link.checkSourceOutDegree();
                    }
                }
            }
            this.stopFiringAction(!this.mouseDrag.active);
            if (this.mouseDrag.active && model) {    // Only update storage if there is an edit
                if (model.constructor.name === 'BasePointModel') {
                    updateLastChange(model.link.props);
                }
                this.saveDatabase();
            }
        } else if (this.state.action instanceof DIAG.MoveCanvasAction) {
            this.stopFiringAction();
        } else {
            diagramEngine.clearRepaintEntities();
            this.stopFiringAction();
        }
        this.mouseDrag.active = false;
        this.mouseDownPos.active = false;
        this.state.document.removeEventListener("mousemove", this.onMouseMove);
        this.state.document.removeEventListener("mouseup", this.onMouseUp);
    }

    /**
     * Block events if canvas is not in focus
     * @param {Object} event React event
     * @returns {boolean}
     */
    onKeyUp(event) {
        const currentViewId = window.apm.cache.get(Constant.k.NAMESPACE_DIAGRAM_WIDGET, "current-view");
        if (currentViewId !== this.props.clientId) {
            event.preventDefault();
            return false;
        }
    }

    /**
     * Wheel event listener attached to this component's DOM ref
     * @param {object} event 
     */
    handleWheel(event){
        const dX = -event.deltaX;
        const dY = -event.deltaY;
        this.getCanvas().css({
            "background-position-x": (parseFloat(this.getCanvas().css("background-position-x")) + dX) + "px",
            "background-position-y": (parseFloat(this.getCanvas().css("background-position-y")) + dY) + "px"
        });
        const diagramModel = this.getFlowModel();
        diagramModel.setOffset(
            diagramModel.getOffsetX() + dX,
            diagramModel.getOffsetY() + dY
        );
        this.getCanvas().children(".link-terminal-view").css("transform",
            "translate(" + diagramModel.getOffsetX() + "px," + diagramModel.getOffsetY() + "px) scale(" + (diagramModel.getZoomLevel() / 100) + ")"
        );
        this.forceUpdate();
    }

    /**
     * React render
     * @returns {XML|JSX.Element}
     */
    render() {
        const diagramEngine = this.getGraphModel().getEngine();
        const diagramModel = diagramEngine.getDiagramModel();
        return (
            <div
                ref={ref => {
                    if (ref) {
                        this.canvasNodeRef = ref;
                        this.canvasNodeRef.addEventListener('wheel', this.handleWheel, { passive: false })
                        this.getGraphModel().getEngine().setCanvas(ref);
                    } else {
                        this.canvasNodeRef.removeEventListener('wheel', this.handleWheel);
                        this.canvasNodeRef = null;
                    }
                }}
                className={"storm-diagrams-canvas id-" + this.props.clientId}
                tabIndex="0"
                
                onContextMenu={event => {
                    event.preventDefault();

                    //prevents nodes from dragging when context menu is called
                    this.mouseDrag.active = false;
                    this.mouseDownPos.active = false;
                    this.state.document.removeEventListener("mousemove", this.onMouseMove);
                    this.state.document.removeEventListener("mouseup", this.onMouseUp);

                    var nodeSelection = [];
                    var linkSelection = [];
                    const selection = diagramModel.getSelectedItems();
                    for (var i = 0; i < selection.length; i++) {
                        const element = selection[i];
                        if (element instanceof DIAG.LinkModel) {
                            linkSelection.push(element);
                        }
                        if (element instanceof DIAG.NodeModel) {
                            nodeSelection.push(element);
                        }
                    }
                    const contextContainer = $(".context_menu");
                    if (nodeSelection.length > 1) {
                        // Multiple-selection menu
                        ReactDOM.render(
                            <ContextMenuWidget
                                type={"multipleSelection"}
                                context={{ node: nodeSelection, link: linkSelection }}
                                domContext={contextContainer}
                                pos={{ left: event.pageX, top: event.pageY }} />,
                            contextContainer.get(0)
                        );
                    } else {
                        // Single-selection menu
                        const element = this.getMouseElement(event);
                        if (!element || !(element.model instanceof DIAG.PortModel)) {
                            ReactDOM.render(
                                <ContextMenuWidget
                                    type={"singleSelection"}
                                    context={element}
                                    domContext={contextContainer}
                                    pos={{ left: event.pageX, top: event.pageY }} />,
                                contextContainer.get(0)
                            );
                        }
                    }
                }}
                onKeyDown={event => {
                    window.apm.printDebug("keydown", event.keyCode, Constant.k.DEBUG_UI);
                   const isCommonModule= window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule;
                    switch (event.keyCode) {
                        case 46:
                        case 8: // del
                            this.handleSelected("delete",event);
                            break;
                        case 32: // space
                            event.preventDefault();
                            if (!this.isSpaceKeyDown) {
                                this.isSpaceKeyDown = true;
                                this.getCanvas().css("cursor", "move");
                            }
                            break;
                        case 65: // a
                            if (this.isCtrlKeyDown) {
                                event.preventDefault();
                                this.handleCustomEvent({ target: "request-select-all" });
                            }
                            break;
                        case 67: // c
                            if (this.isCtrlKeyDown) {
                                event.preventDefault();
                                this.handleCustomEvent({ target: "request-copy" });
                            }
                            break;
                        case 86: // v
                        //if the open tab is common module don't allow the pasting of components
                            if (this.isCtrlKeyDown&&!isCommonModule) {
                                event.preventDefault();
                                this.handleCustomEvent({ target: "request-paste" });
                            }
                            break;
                        case 90: // z
                            if (this.isCtrlKeyDown && !event.shiftKey) {
                                event.preventDefault();
                                this.handleCustomEvent({ target: "request-undo" });
                            }
                            // shift + z
                            if (this.isCtrlKeyDown && event.shiftKey) {
                                event.preventDefault();
                                this.handleCustomEvent({ target: "request-redo" });
                            }
                            break;
                            case 82: // r
                                if (this.isCtrlKeyDown) {
                                    event.preventDefault();
                                    this.handleCustomEvent({ target: "request-reload" });
                                }
                                break;
                        case 17:
                        case 91:
                        case 93: // ctrl / command
                            event.preventDefault();
                            this.isCtrlKeyDown = true;
                            this.getCanvas().css("cursor", "nesw-resize");
                            break;
                        case 189: // -
                            if (this.isCtrlKeyDown) {
                                event.preventDefault();
                                const newZoomLevel = this.getNextZoomLevel(false);
                                this.zoomDiagram(event, newZoomLevel);
                            }
                            break;
                        case 187: // =
                            if (this.isCtrlKeyDown) {
                                event.preventDefault();
                                const newZoomLevel = this.getNextZoomLevel(true);
                                this.zoomDiagram(event, newZoomLevel);
                            }
                            break;
                        default:
                            break;
                    }
                }}
                onKeyUp={event => {
                    switch (event.keyCode) {
                        case 32: // space
                            event.preventDefault();
                            this.isSpaceKeyDown = false;
                            break;
                        case 17:
                        case 91:
                        case 93: // ctrl / command
                            event.preventDefault();
                            this.isCtrlKeyDown = false;
                            break;
                    }
                    this.getCanvas().css("cursor", "default");
                    if (this.props.deleteKeys.indexOf(event.keyCode) !== -1) {
                        this.clearLinkTerminals();
                    }
                }}
                //
                onDoubleClick={event => {
                    var model = this.getMouseElement(event);
                    if (model && model.model instanceof ModuleNodeModel) {
                        model.model.setSelected(false);
                        this.handleCustomEvent({ target: "request-element-error-message", context: model.model })
                        this.handleCustomEvent({ 
                            target: "request-open-module", 
                            event: { 
                                id: model.model.getID(),
                                version: model.model.props.flowVersion || 1
                            } 
                        });
                    }
                    this.hidePropertyPanel();
                }}
                onMouseMove={event => {
                    if (!(event.clientX === this.mouseDrag.x && event.clientY === this.mouseDrag.y)) {
                        this.mouseDrag.active = true;
                    }
                    if (this.mouseDownPos.active) {
                        const dX = event.clientX - this.mouseDownPos.x;
                        const dY = event.clientY - this.mouseDownPos.y;
                        this.getCanvas().css({
                            "background-position-x": (this.mouseDownPos.ox + dX) + "px",
                            "background-position-y": (this.mouseDownPos.oy + dY) + "px"
                        });
                        const diagramModel = this.getFlowModel();
                        this.getCanvas().children(".link-terminal-view").css("transform",
                            "translate(" + diagramModel.getOffsetX() + "px," + diagramModel.getOffsetY() + "px) scale(" + (diagramModel.getZoomLevel() / 100) + ")"
                        );
                    }
                }}
                onMouseDown={event => {
                    $("#tl_container").jstree().deselect_all();
                    $("#tm_container").jstree().deselect_all();
                    this.mouseDrag.active = false;
                    this.mouseDrag.x = event.clientX;
                    this.mouseDrag.y = event.clientY;
                    if (window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule && this.mouseDrag.active) {
                        this.stopFiringAction();
                    }
                    // Re-enable full refresh
                    diagramEngine.clearRepaintEntities();
                    if (this.isSpaceKeyDown || event.button === 1) {
                        this.getCanvas().css("cursor", "move");
                        this.clearLinkTerminals();
                        diagramEngine.enableRepaintEntities([]);
                        //its a drag the canvas event
                        if (!window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule) {
                            this.startFiringAction(new DIAG.MoveCanvasAction(event.clientX, event.clientY, diagramModel));
                            this.mouseDownPos.x = event.clientX;
                            this.mouseDownPos.y = event.clientY;
                            this.mouseDownPos.active = true;
                            this.mouseDownPos.ox = parseFloat(this.getCanvas().css("background-position-x"));
                            this.mouseDownPos.oy = parseFloat(this.getCanvas().css("background-position-y"));
                            this.hidePropertyPanel();
                        }
                    } else {
                        var model = this.getMouseElement(event);
                        window.apm.printDebug("canvas select", model, Constant.k.DEBUG_UI);
                        if (model === null || !model.model) {
                            // Canvas was selected
                            this.clearLinkTerminals();
                            // It's a selection box
                            diagramModel.clearSelection();
                            diagramEngine.enableRepaintEntities([]);
                            const relative = diagramEngine.getRelativePoint(event.clientX, event.clientY);
                            this.startFiringAction(new DIAG.SelectingAction(relative.x, relative.y));
                            this.hidePropertyPanel();
                        }

                        else if (model.model instanceof DIAG.PortModel) {
                            this.clearLinkTerminals();
                            //its a port element, we want to drag a link
                            if (window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule) {
                                return;
                            }
                            if (!this.getGraphModel().getEngine().isModelLocked(model.model)) {
                                const relative = diagramEngine.getRelativeMousePoint(event);
                                const link = new BaseLinkModel(model.model, null);

                                link.getFirstPoint().updateLocation(relative);
                                link.getLastPoint().updateLocation(relative);

                                diagramModel.clearSelection();
                                link.getLastPoint().setSelected(true);
                                diagramModel.addLink(link);

                                this.startFiringAction(new DIAG.MoveItemsAction(event.clientX, event.clientY, diagramEngine));
                            } else {
                                diagramModel.clearSelection();
                            }
                            this.hidePropertyPanel();
                        } else {
                            //its some or other element, probably want to move it
                            if (!(model.model instanceof DIAG.LinkModel)) {
                                if (!(model.model instanceof DIAG.PointModel)) {
                                    this.clearLinkTerminals();
                                } else {
                                    if (this.currentHighlightedLink && model.model.link.getID() !== this.currentHighlightedLink.getID()) {
                                        this.clearLinkTerminals();
                                    }
                                }
                            }
                            if (!event.shiftKey && !model.model.isSelected()) {
                                diagramModel.clearSelection();
                            }
                            // Multiple selection with shift key
                            if (event.shiftKey) {
                                model.model.setSelected(!model.model.selected);
                            } else {
                                model.model.setSelected(true);
                            }

                            // Show property panel on certain elements
                            if (model.model instanceof DIAG.NodeModel) {
                                diagramEngine.enableRepaintEntities(diagramModel.getSelectedItems());
                                this.handleCustomEvent({
                                    target: "request-select-matching",
                                    event: { model: model.model, target: "tree" }
                                });
                                
                                this.showPropertyPanel(model.model, event.clientX);
                            } else if (model.model instanceof DIAG.LinkModel) {
                                this.showPropertyPanel(model.model, event.clientX);
                            } else {
                                this.hidePropertyPanel();
                            }

                            // if selected node would be occluded by property panel, offset the canvas
                            if (this.shouldOffsetCanvasForPropertyPanel(event.clientX)) {

                                this.setCanvasOffsetForPropertyPanel(event.clientX);   

                            } else {

                                this.startFiringAction(new DIAG.MoveItemsAction(event.clientX, event.clientY, diagramEngine));
                            }
                        }
                    }
                    this.state.document.addEventListener("mousemove", this.onMouseMove);
                    this.state.document.addEventListener("mouseup", this.onMouseUp);
                }}
            >
                <CustomNodeLayerWidget diagramEngine={diagramEngine} ref={"nodeLayer-" + this.props.clientId}
                    readOnly={this.props.node.props.readOnly}
                    isCommonModule={this.props.node.props.isCommonModule}
                />
                {this.state.renderedNodes && (
                    <CustomLinkLayerWidget
                        ref={"linkLayer-" + this.props.clientId}
                        diagramEngine={diagramEngine}
                        pointAdded={(point, event) => {
                            this.state.document.addEventListener("mousemove", this.onMouseMove);
                            this.state.document.addEventListener("mouseup", this.onMouseUp);
                            event.stopPropagation();
                            diagramModel.clearSelection(point);
                            this.setState({
                                action: new DIAG.MoveItemsAction(event.clientX, event.clientY, diagramEngine)
                            });
                        }}
                    />
                )}
                <div className="link-terminal-view"></div>
                {this.state.action instanceof DIAG.SelectingAction && this.drawSelectionBox()}
                <div className="error-bar-container">
                    <div className='error-bar'></div>
                </div>
            </div>
        );
    }
}

export default CustomDiagramWidget;
